import {useCrmTab} from '../../hooks/useCrmSection';
import PlantAiSummaryPanel from '../../components/plants/PlantAiSummaryPanel';
import RefillBillingSettings from '../../components/refills/RefillBillingSettings';
import React, { useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  Checkbox,
  Skeleton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  LocalGasStation as GasIcon,
  Warning as WarningIcon,
  Build as MaintenanceIcon,
  FireExtinguisher as VVFIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  LocalShipping as DeliveryIcon,
  Speed as MeterIcon,
  ExpandMore as ExpandMoreIcon,
  Receipt as ReceiptIcon,
  CheckCircle as PaidIcon,
  Cancel as UnpaidIcon,
  Euro as EuroIcon,
  LocalDrink as LitersIcon,
  AccessTime as TimeIcon,
  ShoppingCart as OrderIcon,
  PlayArrow as StartIcon,
  Flag as FlagIcon,
  TrendingFlat as TrendIcon,
  Balance as BalanceIcon,
  WarningAmber as WarningAmberIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  PictureAsPdf as PdfIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { format, isValid, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { plantsApi, maintenancesApi, vvfPracticesApi, metersApi, interventionNotesApi, plantDocumentsApi, meterReadingsApi } from '../../services/api';
import { getApiBaseUrl } from '../../utils/runtimeUrls';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CardMedia from '@mui/material/CardMedia';
import LitersControlTimeline from '../../components/plants/LitersControlTimeline';
import { formatNuf } from '../../utils/formatNuf';
import MobileCardList from '../../components/MobileCardList';

// Helper per formattare date in modo sicuro
const safeFormatDate = (dateValue, formatStr = 'dd/MM/yyyy') => {
  if (!dateValue) return '-';
  try {
    const date = typeof dateValue === 'string' ? parseISO(dateValue) : new Date(dateValue);
    if (!isValid(date)) return '-';
    return format(date, formatStr, { locale: it });
  } catch (e) {
    return '-';
  }
};

const normalizePriceInput = (value) => {
  const normalized = String(value || '').trim().replace(',', '.');
  if (!normalized) return { value: null, error: '' };
  if (!/^\d+(\.\d{1,6})?$/.test(normalized)) {
    return { value: null, error: 'Inserisci un prezzo valido, es. 0,9800' };
  }

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return { value: null, error: 'Il prezzo deve essere maggiore o uguale a zero' };
  }

  return { value: parsed.toFixed(4), error: '' };
};

const formatMeterPrice = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const parsed = Number(String(value).replace(',', '.'));
  if (!Number.isFinite(parsed)) return '-';
  return `€ ${parsed.toFixed(4).replace('.', ',')}`;
};

const getMeterDisplayName = (meter) => (
  meter?.c_nome || meter?.intestatario || meter?.customer?.ragione_sociale || meter?.customer?.nome || '-'
);

const getMeterDisplayPlace = (meter) => (
  meter?.c_localita || meter?.localita || meter?.citta || meter?.c_citta || '-'
);

// Tab Panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Componente singolo gauge per livello
const SingleGauge = ({ percent, liters, capacity, threshold, title, subtitle, color: customColor }) => {
  const isLow = percent <= threshold;
  const color = customColor || (isLow ? 'error' : percent <= 30 ? 'warning' : 'success');
  const strokeColor = customColor === 'info' ? '#2196f3' : (isLow ? '#f44336' : percent <= 30 ? '#ff9800' : '#4caf50');

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      <Box sx={{ position: 'relative', display: 'inline-flex', width: 120, height: 120 }}>
        <svg viewBox="0 0 36 36" style={{ transform: 'rotate(-90deg)' }}>
          <path
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke="#e0e0e0"
            strokeWidth="3"
          />
          <path
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke={strokeColor}
            strokeWidth="3"
            strokeDasharray={`${Math.max(0, Math.min(100, percent))}, 100`}
          />
        </svg>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="h5" component="div" color={`${color}.main`}>
            {percent?.toFixed(0) || 0}%
          </Typography>
        </Box>
      </Box>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        <strong>{liters?.toFixed(0) || 0}</strong> L
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

// Componente doppio gauge per livello (rilevazione + stima)
const DualLevelGauge = ({ lastReading, estimate, capacity, threshold }) => {
  const hasReading = !!lastReading;
  const estimatePercent = estimate?.percent || 0;
  const isLow = estimatePercent <= threshold;

  return (
    <Card sx={{ p: 2, height: '100%' }}>
      <Typography variant="h6" gutterBottom sx={{ textAlign: 'center' }}>
        Livello Serbatoio
      </Typography>
      <Grid container spacing={2} alignItems="center" justifyContent="center">
        {/* Ultima Rilevazione */}
        <Grid item xs={6}>
          {hasReading ? (
            <SingleGauge
              percent={lastReading.percent}
              liters={lastReading.liters}
              capacity={capacity}
              threshold={threshold}
              title="Ultima Rilevazione"
              subtitle={safeFormatDate(lastReading.date)}
              color="success"
            />
          ) : (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Ultima Rilevazione
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: 2 }}>
                Nessuna rilevazione
              </Typography>
            </Box>
          )}
        </Grid>
        
        {/* Stima Attuale */}
        <Grid item xs={6}>
          <SingleGauge
            percent={estimatePercent}
            liters={estimate?.liters || 0}
            capacity={capacity}
            threshold={threshold}
            title="Stima Oggi"
            subtitle="calcolato"
            color={isLow ? 'error' : estimatePercent <= 30 ? 'warning' : 'info'}
          />
        </Grid>
      </Grid>
      
      {isLow && (
        <Alert severity="warning" sx={{ mt: 2 }} icon={<WarningIcon />}>
          Livello sotto la soglia ({threshold}%)
        </Alert>
      )}
      
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
        Capacità totale: {capacity || 0} L
      </Typography>
    </Card>
  );
};

// Manteniamo anche il vecchio LevelGauge per retrocompatibilità
const LevelGauge = ({ percent, liters, capacity, threshold }) => {
  const isLow = percent <= threshold;
  const color = isLow ? 'error' : percent <= 30 ? 'warning' : 'success';

  return (
    <Card sx={{ textAlign: 'center', p: 2 }}>
      <Typography variant="h6" gutterBottom>Livello Serbatoio</Typography>
      <Box sx={{ position: 'relative', display: 'inline-flex', width: 150, height: 150 }}>
        <svg viewBox="0 0 36 36" style={{ transform: 'rotate(-90deg)' }}>
          <path
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke="#e0e0e0"
            strokeWidth="3"
          />
          <path
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke={isLow ? '#f44336' : percent <= 30 ? '#ff9800' : '#4caf50'}
            strokeWidth="3"
            strokeDasharray={`${percent}, 100`}
          />
        </svg>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="h4" component="div" color={`${color}.main`}>
            {percent.toFixed(0)}%
          </Typography>
          {isLow && <WarningIcon color="error" />}
        </Box>
      </Box>
      <Typography variant="body1" sx={{ mt: 1 }}>
        <strong>{liters?.toFixed(0) || 0}</strong> / {capacity || 0} litri
      </Typography>
      {isLow && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Livello sotto la soglia ({threshold}%)
        </Alert>
      )}
    </Card>
  );
};

// Componente Note Intervento per Impianto
const PlantInterventionNotesTab = ({ plantId, metersData }) => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showAddPlantNote, setShowAddPlantNote] = useState(false);
  const [newPlantNoteText, setNewPlantNoteText] = useState('');
  const [showAddMeterNote, setShowAddMeterNote] = useState(null);
  const [newMeterNoteText, setNewMeterNoteText] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [completingNote, setCompletingNote] = useState(null);
  const [completionText, setCompletionText] = useState('');

  // Query note attive
  const { data: notesData, isLoading: notesLoading } = useQuery({
    queryKey: ['intervention-notes', 'plant-meters', plantId],
    queryFn: async () => {
      const response = await interventionNotesApi.getByPlantMeters(plantId);
      return response.data?.data || { meterNotes: [], plantNotes: [] };
    },
    enabled: !!plantId,
  });

  // Query note completate
  const { data: completedData } = useQuery({
    queryKey: ['intervention-notes', 'plant-completed', plantId],
    queryFn: async () => {
      const res = await interventionNotesApi.getAll({ plant_id: plantId, completata: true, limit: 100 });
      return res.data?.data || [];
    },
    enabled: !!plantId && showCompleted,
  });

  const invalidateNotes = () => {
    queryClient.invalidateQueries({ queryKey: ['intervention-notes'] });
  };

  const createMutation = useMutation({
    mutationFn: (data) => interventionNotesApi.create(data),
    onSuccess: () => {
      invalidateNotes();
      setSnackbar({ open: true, message: 'Nota creata con successo', severity: 'success' });
    },
    onError: () => setSnackbar({ open: true, message: 'Errore nella creazione', severity: 'error' }),
  });

  const completeMutation = useMutation({
    mutationFn: ({ noteId, note_completamento }) => interventionNotesApi.complete(noteId, { note_completamento }),
    onSuccess: () => {
      invalidateNotes();
      setCompletingNote(null);
      setCompletionText('');
      setSnackbar({ open: true, message: 'Nota completata', severity: 'success' });
    },
  });

  const reopenMutation = useMutation({
    mutationFn: (noteId) => interventionNotesApi.reopen(noteId),
    onSuccess: () => {
      invalidateNotes();
      setSnackbar({ open: true, message: 'Nota riaperta', severity: 'info' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (noteId) => interventionNotesApi.delete(noteId),
    onSuccess: () => {
      invalidateNotes();
      setSnackbar({ open: true, message: 'Nota eliminata', severity: 'success' });
    },
  });

  const plantNotes = notesData?.plantNotes || [];
  const meterNotes = notesData?.meterNotes || [];

  const meterNotesMap = {};
  meterNotes.forEach(n => {
    if (!meterNotesMap[n.meter_id]) meterNotesMap[n.meter_id] = [];
    meterNotesMap[n.meter_id].push(n);
  });

  const handleAddPlantNote = () => {
    if (!newPlantNoteText.trim()) return;
    createMutation.mutate({
      plant_id: parseInt(plantId),
      tipo: 'impianto',
      nota: newPlantNoteText.trim(),
    });
    setNewPlantNoteText('');
    setShowAddPlantNote(false);
  };

  const handleAddMeterNote = (meterId) => {
    if (!newMeterNoteText.trim()) return;
    createMutation.mutate({
      meter_id: meterId,
      plant_id: parseInt(plantId),
      tipo: 'contatore',
      nota: newMeterNoteText.trim(),
    });
    setNewMeterNoteText('');
    setShowAddMeterNote(null);
  };

  if (notesLoading) return <LinearProgress />;

  return (
    <Box>
      {/* Sezione Note Impianto */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberIcon color="warning" />
            Note Intervento Impianto
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setShowAddPlantNote(!showAddPlantNote)}
            color="warning"
          >
            Aggiungi Nota Impianto
          </Button>
        </Box>

        <Collapse in={showAddPlantNote}>
          <Paper sx={{ p: 2, mb: 2, bgcolor: '#fff3e0', border: '1px solid #ff9800' }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Nota intervento impianto"
              value={newPlantNoteText}
              onChange={(e) => setNewPlantNoteText(e.target.value)}
              placeholder="Descrivi l'intervento da effettuare sull'impianto..."
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" color="warning" size="small" onClick={handleAddPlantNote}
                disabled={!newPlantNoteText.trim() || createMutation.isPending}>
                Salva
              </Button>
              <Button size="small" onClick={() => { setShowAddPlantNote(false); setNewPlantNoteText(''); }}>
                Annulla
              </Button>
            </Box>
          </Paper>
        </Collapse>

        {plantNotes.length === 0 ? (
          <Alert severity="info" variant="outlined">Nessuna nota di intervento per questo impianto</Alert>
        ) : (
          plantNotes.map(note => (
            <Paper key={note.id} sx={{ p: 2, mb: 1, bgcolor: '#fff3e0', borderLeft: '4px solid #ff9800' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1">{note.nota}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Creata il {safeFormatDate(note.data_creazione)}
                    {note.operatore && ` \u2022 ${note.operatore}`}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Segna come completata">
                    <Checkbox
                      size="small"
                      checked={false}
                      onChange={() => { setCompletingNote(note); setCompletionText(''); }}
                    />
                  </Tooltip>
                  <Tooltip title="Elimina">
                    <IconButton size="small" color="error" onClick={() => deleteMutation.mutate(note.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>
          ))
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Sezione Note Contatori */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <MeterIcon color="primary" />
          Note Intervento Contatori
        </Typography>

        {metersData?.length === 0 ? (
          <Alert severity="info" variant="outlined">Nessun contatore associato</Alert>
        ) : (
          metersData?.map(meter => {
            const notes = meterNotesMap[meter.id] || [];
            return (
              <Accordion key={meter.id} defaultExpanded={notes.length > 0}
                sx={notes.length > 0 ? { border: '1px solid #ff9800', '&:before': { display: 'none' } } : {}}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Chip label={meter.meter_number} size="small" color="primary" variant="outlined" />
                    <Typography variant="body2">
                      {meter.c_nome || meter.intestatario || '-'}
                    </Typography>
                    {notes.length > 0 && (
                      <Chip label={`${notes.length} nota/e`} size="small" color="warning" sx={{ ml: 'auto', mr: 2 }} />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {notes.map(note => (
                    <Paper key={note.id} sx={{ p: 1.5, mb: 1, bgcolor: '#fff3e0', borderLeft: '3px solid #ff9800' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2">{note.nota}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {safeFormatDate(note.data_creazione)}
                            {note.operatore && ` \u2022 ${note.operatore}`}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Completata">
                            <Checkbox size="small" checked={false} onChange={() => { setCompletingNote(note); setCompletionText(''); }} />
                          </Tooltip>
                          <Tooltip title="Elimina">
                            <IconButton size="small" color="error" onClick={() => deleteMutation.mutate(note.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Paper>
                  ))}

                  {showAddMeterNote === meter.id ? (
                    <Box sx={{ mt: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                        label="Nota intervento contatore"
                        value={newMeterNoteText}
                        onChange={(e) => setNewMeterNoteText(e.target.value)}
                        placeholder="Descrivi l'intervento..."
                        sx={{ mb: 1 }}
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="contained" color="warning" size="small"
                          onClick={() => handleAddMeterNote(meter.id)}
                          disabled={!newMeterNoteText.trim() || createMutation.isPending}>
                          Salva
                        </Button>
                        <Button size="small" onClick={() => { setShowAddMeterNote(null); setNewMeterNoteText(''); }}>
                          Annulla
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Button size="small" startIcon={<AddIcon />} color="warning"
                      onClick={() => { setShowAddMeterNote(meter.id); setNewMeterNoteText(''); }}>
                      Aggiungi nota
                    </Button>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })
        )}
      </Box>

      {/* Sezione Note Completate */}
      <Box>
        <Button
          size="small"
          variant="text"
          onClick={() => setShowCompleted(!showCompleted)}
          startIcon={<ExpandMoreIcon sx={{ transform: showCompleted ? 'rotate(180deg)' : 'none', transition: '0.3s' }} />}
        >
          {showCompleted ? 'Nascondi' : 'Mostra'} note completate
        </Button>
        <Collapse in={showCompleted}>
          <Box sx={{ mt: 1 }}>
            {completedData?.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Nessuna nota completata</Typography>
            ) : (
              completedData?.map(note => (
                <Paper key={note.id} sx={{ p: 1.5, mb: 1, bgcolor: '#e8f5e9', borderLeft: '3px solid #4caf50', opacity: 0.8 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2">{note.nota}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {note.tipo === 'impianto' ? '📍 Impianto' : `🔢 Contatore ${note.meter?.meter_number || ''}`}
                        {' \u2022 Completata il '}{safeFormatDate(note.data_completamento)}
                      </Typography>
                      {note.note_completamento && (
                        <Typography variant="body2" sx={{ mt: 0.5, color: '#2e7d32', fontStyle: 'italic', fontSize: '0.85rem' }}>
                          💬 {note.note_completamento}
                        </Typography>
                      )}
                    </Box>
                    <Tooltip title="Riapri">
                      <Checkbox size="small" checked={true} onChange={() => reopenMutation.mutate(note.id)} />
                    </Tooltip>
                  </Box>
                </Paper>
              ))
            )}
          </Box>
        </Collapse>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />

      {/* Dialog Completamento Nota */}
      <Dialog open={!!completingNote} onClose={() => setCompletingNote(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Completa Intervento</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1, p: 1.5, bgcolor: '#fff3e0', borderLeft: '4px solid #ff9800', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight="bold">{completingNote?.nota}</Typography>
            <Typography variant="caption" color="text.secondary">
              {completingNote?.tipo === 'impianto' ? '📍 Impianto' : `🔢 Contatore`}
              {' \u2022 Creata il '}{safeFormatDate(completingNote?.data_creazione)}
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Note operative (facoltative)"
            value={completionText}
            onChange={(e) => setCompletionText(e.target.value)}
            placeholder="Descrivi i dettagli dell'operazione effettuata..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompletingNote(null)}>Annulla</Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => completeMutation.mutate({ noteId: completingNote.id, note_completamento: completionText.trim() || null })}
            disabled={completeMutation.isPending}
          >
            Conferma Completamento
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ============================================
// COMPONENTE: PlantDocumentsSection
// ============================================
const PlantDocumentsSection = ({ plantId, excludePwaGroups }) => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [uploadDialog, setUploadDialog] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const { data: documentGroups, isLoading } = useQuery({
    queryKey: ['plant-documents', plantId],
    queryFn: async () => {
      const response = await plantDocumentsApi.getAll(plantId);
      let groups = response.data?.data || [];
      if (excludePwaGroups) {
        groups = groups.filter(g => !g.nome?.includes('PWA'));
      }
      return groups;
    },
  });

  const uploadMutation = useMutation({
    mutationFn: ({ files, nome, descrizione }) =>
      plantDocumentsApi.upload(plantId, files, nome, descrizione),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plant-documents', plantId] });
      setUploadDialog(false);
      setSelectedFiles([]);
      setGroupName('');
      setGroupDescription('');
      setSnackbar({ open: true, message: 'Documenti caricati con successo' });
    },
    onError: (err) => {
      setSnackbar({ open: true, message: `Errore: ${err.response?.data?.message || err.message}` });
    }
  });

  const deleteFileMutation = useMutation({
    mutationFn: (documentId) => plantDocumentsApi.deleteFile(plantId, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plant-documents', plantId] });
      setSnackbar({ open: true, message: 'File eliminato' });
    }
  });

  const deleteGroupMutation = useMutation({
    mutationFn: (groupId) => plantDocumentsApi.deleteGroup(plantId, groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plant-documents', plantId] });
      setSnackbar({ open: true, message: 'Gruppo eliminato' });
    }
  });

  const handleUpload = () => {
    if (selectedFiles.length === 0) return;
    uploadMutation.mutate({
      files: Array.from(selectedFiles),
      nome: groupName,
      descrizione: groupDescription
    });
  };

  const handleDownload = async (doc) => {
    try {
      const response = await plantDocumentsApi.download(plantId, doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({ open: true, message: 'Errore download file' });
    }
  };

  const handlePreview = async (doc) => {
    try {
      const response = await plantDocumentsApi.view(plantId, doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: doc.mimetype }));
      setPreviewDoc(doc);
      setPreviewUrl(url);
    } catch (err) {
      setSnackbar({ open: true, message: 'Errore apertura anteprima' });
    }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewDoc(null);
    setPreviewUrl(null);
  };

  const getFileIcon = (mimetype) => {
    if (mimetype?.startsWith('image/')) return <ImageIcon sx={{ color: '#4caf50' }} />;
    if (mimetype?.startsWith('video/')) return <VideoIcon sx={{ color: '#9c27b0' }} />;
    if (mimetype === 'application/pdf') return <PdfIcon sx={{ color: '#f44336' }} />;
    return <FileIcon sx={{ color: '#757575' }} />;
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isPreviewable = (mimetype) => {
    return mimetype?.startsWith('image/') || mimetype?.startsWith('video/') || mimetype === 'application/pdf';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          <AttachFileIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Documenti
        </Typography>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={() => setUploadDialog(true)}
        >
          Carica Documenti
        </Button>
      </Box>

      {isLoading ? (
        <LinearProgress />
      ) : documentGroups?.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <AttachFileIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
          <Typography color="text.secondary">Nessun documento caricato</Typography>
        </Paper>
      ) : (
        documentGroups?.map(group => (
          <Paper key={group.id} sx={{ p: 2, mb: 2, borderLeft: '4px solid #1976d2' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">{group.nome}</Typography>
                {group.descrizione && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{group.descrizione}</Typography>
                )}
              </Box>
              <Tooltip title="Elimina gruppo">
                <IconButton size="small" color="error" onClick={() => {
                  if (window.confirm(`Eliminare il gruppo "${group.nome}" e tutti i suoi file?`)) {
                    deleteGroupMutation.mutate(group.id);
                  }
                }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {group.documents?.map(doc => (
                <Paper
                  key={doc.id}
                  variant="outlined"
                  sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1, minWidth: 200, maxWidth: 350 }}
                >
                  {getFileIcon(doc.mimetype)}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap title={doc.filename}>{doc.filename}</Typography>
                    <Typography variant="caption" color="text.secondary">{formatSize(doc.size)}</Typography>
                  </Box>
                  {isPreviewable(doc.mimetype) && (
                    <Tooltip title="Anteprima">
                      <IconButton size="small" onClick={() => handlePreview(doc)}>
                        <FileIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Scarica">
                    <IconButton size="small" onClick={() => handleDownload(doc)}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Elimina">
                    <IconButton size="small" color="error" onClick={() => {
                      if (window.confirm(`Eliminare "${doc.filename}"?`)) {
                        deleteFileMutation.mutate(doc.id);
                      }
                    }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Paper>
              ))}
            </Box>
          </Paper>
        ))
      )}

      {/* Dialog Upload */}
      <Dialog open={uploadDialog} onClose={() => setUploadDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Carica Documenti</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nome"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            sx={{ mt: 2, mb: 2 }}
            placeholder="Es: Certificato conformità, Foto impianto..."
          />
          <TextField
            fullWidth
            label="Descrizione (opzionale)"
            value={groupDescription}
            onChange={(e) => setGroupDescription(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          <Button
            variant="outlined"
            component="label"
            fullWidth
            startIcon={<CloudUploadIcon />}
            sx={{ mb: 1, py: 2, borderStyle: 'dashed' }}
          >
            {selectedFiles.length > 0
              ? `${selectedFiles.length} file selezionati`
              : 'Seleziona file (PDF, immagini, video)'}
            <input
              type="file"
              hidden
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.mp4,.webm,.mov,.avi"
              onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
            />
          </Button>
          {selectedFiles.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {selectedFiles.map((file, idx) => (
                <Typography key={idx} variant="caption" display="block" color="text.secondary">
                  📎 {file.name} ({formatSize(file.size)})
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadDialog(false); setSelectedFiles([]); setGroupName(''); setGroupDescription(''); }}>
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={selectedFiles.length === 0 || uploadMutation.isPending}
          >
            {uploadMutation.isPending ? 'Caricamento...' : 'Carica'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Anteprima */}
      <Dialog open={!!previewDoc} onClose={closePreview} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {previewDoc?.filename}
          <IconButton onClick={closePreview}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', minHeight: 400 }}>
          {previewDoc?.mimetype?.startsWith('image/') && (
            <img src={previewUrl} alt={previewDoc?.filename} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
          )}
          {previewDoc?.mimetype?.startsWith('video/') && (
            <video src={previewUrl} controls style={{ maxWidth: '100%', maxHeight: '70vh' }} />
          )}
          {previewDoc?.mimetype === 'application/pdf' && (
            <iframe src={previewUrl} title={previewDoc?.filename} style={{ width: '100%', height: '70vh', border: 'none' }} />
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

// ============================================
// COMPONENTE: PlantPwaPhotosTab
// ============================================
const API_BASE_URL = getApiBaseUrl();

const PlantPwaPhotosTab = ({ plantId, plantLatitude, plantLongitude }) => {
  const queryClient = useQueryClient();
  const [photoDialog, setPhotoDialog] = useState({ open: false, photos: [], index: 0 });
  const [expandedMeters, setExpandedMeters] = useState({});
  const [uploadTarget, setUploadTarget] = useState(null); // null | { type: 'plant' } | { type: 'meter', meterId: N }

  const photosQuery = useQuery({
    queryKey: ['plant-pwa-photos', plantId],
    queryFn: () => meterReadingsApi.getPlantPhotos(plantId).then(res => res.data?.data),
    enabled: !!plantId
  });

  const deleteMutation = useMutation({
    mutationFn: (photoId) => meterReadingsApi.deletePhoto(photoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plant-pwa-photos', plantId] });
    }
  });

  const uploadMutation = useMutation({
    mutationFn: ({ type, id, formData }) => {
      if (type === 'plant') return meterReadingsApi.uploadPlantPhoto(id, formData);
      return meterReadingsApi.uploadMeterPhoto(id, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plant-pwa-photos', plantId] });
      setUploadTarget(null);
    }
  });

  const handleUpload = (files) => {
    if (!files || files.length === 0 || !uploadTarget) return;
    const formData = new FormData();
    Array.from(files).forEach(f => formData.append('photos', f));
    if (uploadTarget.type === 'plant') {
      uploadMutation.mutate({ type: 'plant', id: plantId, formData });
    } else {
      uploadMutation.mutate({ type: 'meter', id: uploadTarget.meterId, formData });
    }
  };

  const data = photosQuery.data;
  const plantPhotos = data?.plant_photos || [];
  const meterGroups = data?.meter_groups || [];
  const totalPhotos = plantPhotos.length + meterGroups.reduce((sum, g) => sum + g.photos.length, 0);

  if (photosQuery.isLoading) return <LinearProgress />;
  if (photosQuery.isError) return <Alert severity="error">Errore nel caricamento foto: {photosQuery.error?.message}</Alert>;

  const formatPhotoDate = (dateValue) => {
    if (!dateValue) return '-';
    try {
      let d = dateValue;
      if (typeof d === 'string' && !d.endsWith('Z') && !d.includes('+')) d += 'Z';
      const date = new Date(d);
      if (isNaN(date.getTime())) return '-';
      return format(date, 'dd/MM/yyyy HH:mm', { locale: it });
    } catch { return '-'; }
  };

  const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const toRad = (v) => (v * Math.PI) / 180;
    const R = 6371000;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const formatDistance = (photoLat, photoLng) => {
    if (!photoLat || !photoLng || !plantLatitude || !plantLongitude) return null;
    const dist = haversineDistance(parseFloat(photoLat), parseFloat(photoLng), parseFloat(plantLatitude), parseFloat(plantLongitude));
    if (isNaN(dist)) return null;
    return dist < 1000 ? `${Math.round(dist)} m` : `${(dist / 1000).toFixed(1)} km`;
  };

  // Render singola card foto (riutilizzato per impianto e contatori)
  const renderPhotoCard = (photo, idx, allPhotos) => {
    const dist = formatDistance(photo.gps_latitude, photo.gps_longitude);
    return (
      <Grid item key={photo.id}>
        <Card
          sx={{ width: 200, cursor: 'pointer', '&:hover': { boxShadow: 4 }, transition: 'box-shadow 0.2s' }}
          onClick={() => setPhotoDialog({ open: true, photos: allPhotos, index: idx })}
        >
          <CardMedia
            component="img"
            height="130"
            image={`${API_BASE_URL}/meter-readings/photo/${photo.filename}`}
            alt={photo.original_name || 'Foto'}
            sx={{ objectFit: 'cover' }}
            onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="130"><rect fill="%23eee" width="200" height="130"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999" font-size="12">Foto</text></svg>'; }}
          />
          <Box sx={{ px: 1, py: 0.5, bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {formatPhotoDate(photo.created_at)}
                </Typography>
              </Box>
              <Tooltip title="Elimina foto">
                <IconButton size="small" color="error" onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm('Eliminare questa foto?')) deleteMutation.mutate(photo.id);
                }}>
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
            {(photo.gps_latitude && photo.gps_longitude) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <LocationOnIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  {parseFloat(photo.gps_latitude).toFixed(5)}, {parseFloat(photo.gps_longitude).toFixed(5)}
                  {' '}
                  <a
                    href={`https://maps.google.com/?q=${photo.gps_latitude},${photo.gps_longitude}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ color: '#1976d2', fontSize: 10 }}
                    onClick={(e) => e.stopPropagation()}
                  >Maps</a>
                </Typography>
                {dist && (
                  <Chip label={`↔ ${dist}`} size="small" sx={{ height: 18, fontSize: 10, ml: 0.5 }} color={parseFloat(dist) > 5 ? 'warning' : 'default'} />
                )}
                {photo.gps_accuracy && (
                  <Chip label={`GPS ±${Math.round(parseFloat(photo.gps_accuracy))}m`} size="small" sx={{ height: 18, fontSize: 10, ml: 0.5 }} color={parseFloat(photo.gps_accuracy) <= 10 ? 'success' : parseFloat(photo.gps_accuracy) <= 50 ? 'warning' : 'error'} />
                )}
              </Box>
            )}
          </Box>
        </Card>
      </Grid>
    );
  };

  const toggleMeterExpand = (meterId) => {
    setExpandedMeters(prev => ({ ...prev, [meterId]: !prev[meterId] }));
  };

  return (
    <Box>
      {/* Foto Impianto */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            <PhotoLibraryIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
            Foto impianto ({plantPhotos.length})
          </Typography>
          <Button
            size="small"
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
          >
            Carica foto
            <input type="file" hidden multiple accept="image/*"
              onChange={(e) => { setUploadTarget({ type: 'plant' }); handleUpload(e.target.files); }}
            />
          </Button>
        </Box>
        {plantPhotos.length > 0 ? (
          <Grid container spacing={1.5}>
            {plantPhotos.map((photo, idx) => renderPhotoCard(photo, idx, plantPhotos))}
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>Nessuna foto impianto</Typography>
        )}
        {uploadMutation.isPending && uploadTarget?.type === 'plant' && <LinearProgress sx={{ mt: 1 }} />}
      </Paper>

      {/* Foto Contatori - un box per contatore con ultima foto + espandibile */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          <CameraAltIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
          Foto contatori ({meterGroups.reduce((sum, g) => sum + g.photos.length, 0)})
        </Typography>
        {meterGroups.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>Nessuna foto contatori</Typography>
        )}
        {meterGroups.map((group) => {
          const meterId = group.meter?.id;
          const isExpanded = expandedMeters[meterId];
          const lastPhoto = group.photos[0];
          const hasMore = group.photos.length > 1;

          return (
            <Box key={meterId || Math.random()} sx={{ mb: 2 }}>
              {/* Header contatore */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip
                  label={group.meter?.meter_number || 'N/D'}
                  size="small"
                  color="primary"
                  variant="outlined"
                  component={RouterLink}
                  to={`/crm/contatori/${meterId}`}
                  clickable
                  sx={{ textDecoration: 'none' }}
                />
                {group.meter?.customer && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {`${group.meter.customer.cognome || ''} ${group.meter.customer.nome || ''}`.trim()}
                  </Typography>
                )}
                <Button
                  size="small"
                  variant="text"
                  component="label"
                  sx={{ minWidth: 32, p: 0.5, ml: 'auto' }}
                >
                  <CloudUploadIcon fontSize="small" />
                  <input type="file" hidden multiple accept="image/*"
                    onChange={(e) => { setUploadTarget({ type: 'meter', meterId }); handleUpload(e.target.files); }}
                  />
                </Button>
              </Box>

              {/* Ultima foto (stessa card 200x130 delle foto impianto) */}
              {lastPhoto && (
                <Grid container spacing={1.5} alignItems="flex-start">
                  {renderPhotoCard(lastPhoto, 0, group.photos)}
                  {hasMore && (
                    <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                      <Button
                        size="small"
                        onClick={() => toggleMeterExpand(meterId)}
                        sx={{ textTransform: 'none' }}
                        endIcon={isExpanded ? <ExpandMoreIcon sx={{ transform: 'rotate(180deg)' }} /> : <ExpandMoreIcon />}
                      >
                        {isExpanded ? 'Chiudi' : `Altre ${group.photos.length - 1} foto`}
                      </Button>
                    </Grid>
                  )}
                </Grid>
              )}

              {/* Espandi per vedere tutte le foto restanti */}
              {hasMore && (
                <Collapse in={isExpanded}>
                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    {group.photos.slice(1).map((photo, pIdx) =>
                      renderPhotoCard(photo, pIdx + 1, group.photos)
                    )}
                  </Grid>
                </Collapse>
              )}

              {uploadMutation.isPending && uploadTarget?.type === 'meter' && uploadTarget?.meterId === meterId && (
                <LinearProgress sx={{ mt: 1 }} />
              )}
              <Divider sx={{ mt: 2 }} />
            </Box>
          );
        })}
      </Paper>

      {totalPhotos === 0 && (
        <Alert severity="info">Nessuna foto PWA per questo impianto. Usa i pulsanti "Carica foto" per aggiungerne.</Alert>
      )}

      {/* Photo Lightbox Dialog */}
      <Dialog
        open={photoDialog.open}
        onClose={() => setPhotoDialog({ open: false, photos: [], index: 0 })}
        maxWidth="md" fullWidth
      >
        <DialogTitle component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1">
            Foto {photoDialog.index + 1} di {photoDialog.photos.length}
            {photoDialog.photos[photoDialog.index]?.original_name ? ` — ${photoDialog.photos[photoDialog.index].original_name}` : ''}
          </Typography>
          <IconButton onClick={() => setPhotoDialog({ open: false, photos: [], index: 0 })}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1 }}>
          {photoDialog.photos[photoDialog.index] && (
            <img
              src={`${API_BASE_URL}/meter-readings/photo/${photoDialog.photos[photoDialog.index]?.filename}`}
              alt={photoDialog.photos[photoDialog.index]?.original_name || 'Foto'}
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
        {photoDialog.photos.length > 1 && (
          <DialogActions sx={{ justifyContent: 'center' }}>
            <Button disabled={photoDialog.index === 0} onClick={() => setPhotoDialog(p => ({ ...p, index: p.index - 1 }))}>← Precedente</Button>
            <Button disabled={photoDialog.index >= photoDialog.photos.length - 1} onClick={() => setPhotoDialog(p => ({ ...p, index: p.index + 1 }))}>Successiva →</Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

const PlantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Stati
  const [tabValue, setTabValue] = useCrmTab(['interventi','vvf','contatori','livelli','consumi','rifornimenti','controllo-litri','foto']);
  const [levelDialog, setLevelDialog] = useState(false);
  const [newLevel, setNewLevel] = useState({ liters: '', reason: '' });
  const [maintenanceDialog, setMaintenanceDialog] = useState(false);
  const [newMaintenance, setNewMaintenance] = useState({
    tipo: 'ordinaria',
    data_manutenzione: format(new Date(), 'yyyy-MM-dd'),
    tecnico: '',
    descrizione: '',
    esito: 'positivo',
  });
  const [vvfDialog, setVvfDialog] = useState(false);
  const [newVVF, setNewVVF] = useState({
    tipo_pratica: 'nuova_installazione',
    stato: 'da_presentare',
    numero_pratica: '',
    data_presentazione: '',
    data_scadenza: '',
    comando_vvf: '',
    professionista: '',
  });
  const [readingDialog, setReadingDialog] = useState(false);
  const [newReading, setNewReading] = useState({
    reading_date: format(new Date(), 'yyyy-MM-dd'),
    percentage: '',
    technician: '',
    notes: '',
  });
  
  // Stati per ordinamento tabella contatori
  const [metersOrderBy, setMetersOrderBy] = useState('sotto_zona');
  const [metersOrder, setMetersOrder] = useState('asc');
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [bulkPrice, setBulkPrice] = useState('');
  const [selectedMeterIds, setSelectedMeterIds] = useState([]);
  const [priceSnackbar, setPriceSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Filtri data per tab Consumi
  const [consumptionStartDate, setConsumptionStartDate] = useState('');
  const [consumptionEndDate, setConsumptionEndDate] = useState('');

  // Filtri data per tab Rifornimenti
  const [deliveriesStartDate, setDeliveriesStartDate] = useState('');
  const [deliveriesEndDate, setDeliveriesEndDate] = useState('');

  // Query principale impianto
  const { data: plantData, isLoading, error } = useQuery({
    queryKey: ['plant', id],
    queryFn: async () => {
      const response = await plantsApi.getById(id);
      return response.data?.data;
    },
  });

  // Query storico livelli
  const { data: levelHistoryData } = useQuery({
    queryKey: ['plant-level-history', id],
    queryFn: async () => {
      const response = await plantsApi.getLevelHistory(id, { limit: 20 });
      return response.data?.data || [];
    },
    enabled: tabValue === 3,
  });

  // Query dettagli livello (rifornimenti e consumi)
  const { data: levelDetailsData, refetch: refetchLevelDetails } = useQuery({
    queryKey: ['plant-level-details', id],
    queryFn: async () => {
      const response = await plantsApi.getLevelDetails(id);
      return response.data?.data || null;
    },
    enabled: tabValue === 3,
  });

  // Query rifornimenti
  const { data: deliveriesData } = useQuery({
    queryKey: ['plant-deliveries', id],
    queryFn: async () => {
      const response = await plantsApi.getDeliveries(id, { limit: 20 });
      return response.data?.data || [];
    },
    enabled: tabValue === 3,
  });

  // Query contatori dell'impianto
  const { data: metersData } = useQuery({
    queryKey: ['plant-meters', id],
    queryFn: async () => {
      const response = await metersApi.getByPlant(id);
      return response.data || [];
    },
    enabled: tabValue === 0 || tabValue === 2,
  });

  const sortedMeters = React.useMemo(() => {
    const getValue = (obj, key) => {
      if (key === 'sotto_zona') return obj.sotto_zona || obj.c_sotto_zo || 0;
      if (key === 'c_nome') return getMeterDisplayName(obj);
      if (key === 'c_localita') return getMeterDisplayPlace(obj);
      if (key === 'ultima_lettura') return parseFloat(obj.ultima_lettura) || 0;
      if (key === 'consumo_reale') return parseFloat(obj.consumo_reale) || 0;
      if (key === 'prezzo_litro') return parseFloat(obj.prezzo_litro) || 0;
      if (key === 'data_ultima_lettura') return obj.data_ultima_lettura ? new Date(obj.data_ultima_lettura).getTime() : 0;
      return obj[key] || '';
    };

    return [...(metersData || [])].sort((a, b) => {
      const aVal = getValue(a, metersOrderBy);
      const bVal = getValue(b, metersOrderBy);
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return metersOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      return metersOrder === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [metersData, metersOrderBy, metersOrder]);

  // Query consumi/fatture dell'impianto
  const { data: consumptionData, isLoading: isLoadingConsumption } = useQuery({
    queryKey: ['plant-consumption', id, consumptionStartDate, consumptionEndDate],
    queryFn: async () => {
      const params = {};
      if (consumptionStartDate) params.startDate = consumptionStartDate;
      if (consumptionEndDate) params.endDate = consumptionEndDate;
      const response = await plantsApi.getConsumption(id, params);
      return response.data?.data || { years: [], totals: {} };
    },
    enabled: tabValue === 4,
  });

  // Query rifornimenti raggruppati dell'impianto
  const { data: deliveriesGroupedData, isLoading: isLoadingDeliveriesGrouped } = useQuery({
    queryKey: ['plant-deliveries-grouped', id, deliveriesStartDate, deliveriesEndDate],
    queryFn: async () => {
      const params = {};
      if (deliveriesStartDate) params.startDate = deliveriesStartDate;
      if (deliveriesEndDate) params.endDate = deliveriesEndDate;
      const response = await plantsApi.getDeliveriesGrouped(id, params);
      return response.data?.data || { years: [], totals: {} };
    },
    enabled: tabValue === 5,
  });

  // Query per info livello (calcolo automatico)
  const { data: levelInfoData, isLoading: isLoadingLevelInfo, refetch: refetchLevelInfo } = useQuery({
    queryKey: ['plant-level-info', id],
    queryFn: async () => {
      const response = await plantsApi.getLevelInfo(id);
      return response.data;
    },
    enabled: !!id,
  });

  // Query per stima esaurimento gas - sempre attiva per aggiornare gauge e alert
  const { data: forecastData, isLoading: isLoadingForecast } = useQuery({
    queryKey: ['plant-consumption-forecast', id],
    queryFn: async () => {
      const response = await plantsApi.getConsumptionForecast(id);
      return response.data?.data;
    },
    enabled: !!id,
    refetchOnWindowFocus: true,
    staleTime: 30000, // 30 secondi
  });

  // Calcolo urgenza corretto basato su giacenza stimata
  const calculatedUrgency = React.useMemo(() => {
    if (!forecastData?.canEstimate || !plantData) {
      return { urgency: 'normal', message: 'Stima non disponibile' };
    }
    
    const cap = parseFloat(plantData.capacita_totale) || 0;
    const readingLiters = forecastData.lastReading?.liters || 0;
    const daysSinceReading = forecastData.lastReading?.daysSince || 0;
    const avgDailyConsumption = forecastData.estimate?.avgDailyConsumption || 0;
    const thresholdValue = parseFloat(plantData.low_level_threshold) || 15;
    
    // Totale litri aggiunti (rifornimenti + ordini)
    const totalDeliveries = (forecastData.deliveries || []).reduce((sum, d) => sum + (d.quantity_liters || 0), 0);
    const totalOrders = (forecastData.pendingOrders || []).reduce((sum, o) => sum + (o.quantity_liters || 0), 0);
    const totalAddedLiters = totalDeliveries + totalOrders;
    
    // Giacenza attuale stimata
    const giacenzaDopoAggiunte = readingLiters + totalAddedLiters;
    const consumoStimatoTotale = avgDailyConsumption * daysSinceReading;
    const giacenzaAttualeStimata = Math.max(0, giacenzaDopoAggiunte - consumoStimatoTotale);
    
    // Calcolo giorni alla soglia
    const sogliaLitri = (cap * thresholdValue) / 100;
    const litriFinoSoglia = giacenzaAttualeStimata - sogliaLitri;
    const giorniAlSoglia = avgDailyConsumption > 0 ? Math.max(0, Math.floor(litriFinoSoglia / avgDailyConsumption)) : 999;
    
    // Determina urgenza
    if (giorniAlSoglia <= 0) {
      return { urgency: 'critical', message: 'Sotto soglia minima! Rifornimento urgente!' };
    } else if (giorniAlSoglia <= 7) {
      return { urgency: 'critical', message: `Rifornimento urgente entro ${giorniAlSoglia} giorni` };
    } else if (giorniAlSoglia <= 14) {
      return { urgency: 'high', message: `Programmare rifornimento entro ${giorniAlSoglia} giorni` };
    } else if (giorniAlSoglia <= 30) {
      return { urgency: 'medium', message: `Rifornimento consigliato entro ${giorniAlSoglia} giorni` };
    }
    return { urgency: 'normal', message: 'Situazione normale' };
  }, [forecastData, plantData]);

  // Query per rilevazioni tecnico
  const { data: readingsData } = useQuery({
    queryKey: ['plant-readings', id],
    queryFn: async () => {
      const response = await plantsApi.getReadings(id);
      return response.data?.data || [];
    },
    enabled: tabValue === 3,
  });

  // Query per stima livello corrente (basata su ultima rilevazione)
  const { data: currentEstimateData } = useQuery({
    queryKey: ['plant-current-estimate', id],
    queryFn: async () => {
      const response = await plantsApi.getCurrentEstimate(id);
      return response.data?.data;
    },
  });

  // Calcolo stima corretta per il gauge (basata su rilevazione + rifornimenti + ordini - consumo stimato)
  const calculatedEstimate = React.useMemo(() => {
    if (!forecastData?.canEstimate || !plantData) {
      return currentEstimateData?.estimate || { percent: 0, liters: 0 };
    }
    
    const cap = parseFloat(plantData.capacita_totale) || 0;
    const readingLiters = forecastData.lastReading?.liters || 0;
    const daysSinceReading = forecastData.lastReading?.daysSince || 0;
    const avgDailyConsumption = forecastData.estimate?.avgDailyConsumption || 0;
    
    // Totale litri aggiunti (rifornimenti + ordini)
    const totalDeliveries = (forecastData.deliveries || []).reduce((sum, d) => sum + (d.quantity_liters || 0), 0);
    const totalOrders = (forecastData.pendingOrders || []).reduce((sum, o) => sum + (o.quantity_liters || 0), 0);
    const totalAddedLiters = totalDeliveries + totalOrders;
    
    // Giacenza attuale stimata
    const giacenzaDopoAggiunte = readingLiters + totalAddedLiters;
    const consumoStimatoTotale = avgDailyConsumption * daysSinceReading;
    const giacenzaAttualeStimata = Math.max(0, giacenzaDopoAggiunte - consumoStimatoTotale);
    const percentualeAttualeStimata = cap > 0 ? (giacenzaAttualeStimata / cap) * 100 : 0;
    
    return {
      percent: percentualeAttualeStimata,
      liters: giacenzaAttualeStimata
    };
  }, [forecastData, plantData, currentEstimateData]);

  // Mutation per impostare livello
  const setLevelMutation = useMutation({
    mutationFn: (data) => plantsApi.setLevel(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['plant', id]);
      queryClient.invalidateQueries(['plant-level-history', id]);
      queryClient.invalidateQueries(['plant-ai-summary', id]);
      setLevelDialog(false);
      setNewLevel({ liters: '', reason: '' });
    },
  });

  // Mutation per ricalcolare livello
  const recalculateLevelMutation = useMutation({
    mutationFn: () => plantsApi.recalculateLevel(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['plant', id]);
      queryClient.invalidateQueries(['plant-level-info', id]);
      queryClient.invalidateQueries(['plant-level-history', id]);
    },
  });

  // Mutation per creare manutenzione
  const createMaintenanceMutation = useMutation({
    mutationFn: (data) => maintenancesApi.create({ ...data, plant_id: parseInt(id) }),
    onSuccess: () => {
      queryClient.invalidateQueries(['plant', id]);
      queryClient.invalidateQueries(['plant-ai-summary', id]);
      setMaintenanceDialog(false);
      setNewMaintenance({
        tipo: 'ordinaria',
        data_manutenzione: format(new Date(), 'yyyy-MM-dd'),
        tecnico: '',
        descrizione: '',
        esito: 'positivo',
      });
    },
  });

  // Mutation per creare pratica VVF
  const createVVFMutation = useMutation({
    mutationFn: (data) => vvfPracticesApi.create({ ...data, plant_id: parseInt(id) }),
    onSuccess: () => {
      queryClient.invalidateQueries(['plant', id]);
      queryClient.invalidateQueries(['plant-ai-summary', id]);
      setVvfDialog(false);
    },
  });

  // Mutation per aggiungere rilevazione tecnico
  const addReadingMutation = useMutation({
    mutationFn: (data) => plantsApi.addReading(id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['plant', id]);
      queryClient.invalidateQueries(['plant-readings', id]);
      queryClient.invalidateQueries(['plant-level-info', id]);
      queryClient.invalidateQueries(['plant-level-history', id]);
      queryClient.invalidateQueries(['plant-current-estimate', id]);
      queryClient.invalidateQueries(['plant-ai-summary', id]);
      setReadingDialog(false);
      setNewReading({
        reading_date: format(new Date(), 'yyyy-MM-dd'),
        percentage: '',
        technician: '',
        notes: '',
      });
      // Se c'è anomalia, mostra alert
      if (response.data?.anomaly) {
        alert(response.data.message);
      }
    },
  });

  // Mutation per eliminare rilevazione tecnico
  const deleteReadingMutation = useMutation({
    mutationFn: (readingId) => plantsApi.deleteReading(id, readingId),
    onSuccess: () => {
      queryClient.invalidateQueries(['plant', id]);
      queryClient.invalidateQueries(['plant-readings', id]);
      queryClient.invalidateQueries(['plant-level-info', id]);
      queryClient.invalidateQueries(['plant-level-history', id]);
      queryClient.invalidateQueries(['plant-current-estimate', id]);
      queryClient.invalidateQueries(['plant-ai-summary', id]);
    },
  });

  // Mutation per eliminare manutenzione
  const deleteMaintenanceMutation = useMutation({
    mutationFn: (maintenanceId) => maintenancesApi.delete(maintenanceId),
    onSuccess: () => {
      queryClient.invalidateQueries(['plant', id]);
      queryClient.invalidateQueries(['plant-ai-summary', id]);
    },
  });

  const bulkPriceValidation = normalizePriceInput(bulkPrice);
  const allMetersSelected = sortedMeters.length > 0 && selectedMeterIds.length === sortedMeters.length;
  const someMetersSelected = selectedMeterIds.length > 0 && selectedMeterIds.length < sortedMeters.length;

  const bulkPriceMutation = useMutation({
    mutationFn: ({ meterIds, prezzoLitro }) => metersApi.bulkUpdatePrice({
      plant_id: parseInt(id, 10),
      meter_ids: meterIds,
      prezzo_litro: prezzoLitro,
    }),
    onSuccess: (response) => {
      const updatedCount = response.data?.updated_count ?? selectedMeterIds.length;
      queryClient.invalidateQueries({ queryKey: ['plant-meters', id] });
      queryClient.invalidateQueries({ queryKey: ['plant-ai-summary', id] });
      setPriceDialogOpen(false);
      setBulkPrice('');
      setSelectedMeterIds([]);
      setPriceSnackbar({
        open: true,
        severity: 'success',
        message: `${updatedCount} contatori aggiornati a ${formatMeterPrice(response.data?.prezzo_litro)}`,
      });
    },
    onError: (err) => {
      setPriceSnackbar({
        open: true,
        severity: 'error',
        message: err.response?.data?.message || err.message || 'Errore aggiornamento prezzi contatori',
      });
    },
  });

  const handleOpenPriceDialog = () => {
    setSelectedMeterIds(sortedMeters.map((meter) => meter.id));
    setBulkPrice('');
    setPriceDialogOpen(true);
  };

  const handleClosePriceDialog = () => {
    if (bulkPriceMutation.isPending) return;
    setPriceDialogOpen(false);
  };

  const handleToggleAllMeters = (checked) => {
    setSelectedMeterIds(checked ? sortedMeters.map((meter) => meter.id) : []);
  };

  const handleToggleMeter = (meterId) => {
    setSelectedMeterIds((prev) => (
      prev.includes(meterId) ? prev.filter((idValue) => idValue !== meterId) : [...prev, meterId]
    ));
  };

  const handleApplyBulkPrice = () => {
    if (!bulkPriceValidation.value || selectedMeterIds.length === 0) return;
    bulkPriceMutation.mutate({
      meterIds: selectedMeterIds,
      prezzoLitro: bulkPriceValidation.value,
    });
  };

  if (isLoading) {
    return <LinearProgress />;
  }

  if (error || !plantData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Errore nel caricamento dell'impianto: {error?.message || 'Non trovato'}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/crm/impianti')} sx={{ mt: 2 }}>
          Torna alla lista
        </Button>
      </Box>
    );
  }

  const plant = plantData;
  const currentPercent = parseFloat(plant.current_level_percent) || 0;
  const currentLiters = parseFloat(plant.current_level_liters) || 0;
  const capacity = parseFloat(plant.capacita_totale) || 0;
  const threshold = parseFloat(plant.low_level_threshold) || 15;

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2, minWidth: 0, flex: 1 }}>
          <IconButton onClick={() => navigate('/crm/impianti')} size={isMobile ? 'small' : 'medium'}>
            <BackIcon />
          </IconButton>
          {!isMobile && <GasIcon sx={{ fontSize: 40, color: 'primary.main' }} />}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant={isMobile ? 'h6' : 'h4'} component="h1" noWrap>
                {plant.plant_code}
                {plant.nome && ` - ${plant.nome}`}
              </Typography>
              {!isMobile && plant.delivery_code && (
                <Chip
                  icon={<DeliveryIcon />}
                  label={`Consegna: ${plant.delivery_code}`}
                  color="info"
                  variant="outlined"
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" noWrap>
              {[plant.indirizzo, plant.cap, plant.citta, plant.provincia].filter(Boolean).join(', ') || 'Indirizzo non specificato'}
            </Typography>
          </Box>
          <Chip
            label={plant.attivo ? 'Attivo' : 'Inattivo'}
            color={plant.attivo ? 'success' : 'default'}
            size="small"
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            width: { xs: '100%', sm: 'auto' },
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setLevelDialog(true)}
            size={isMobile ? 'small' : 'medium'}
            sx={{ flex: { xs: '1 1 0', sm: '0 0 auto' } }}
          >
            {isMobile ? 'Livello' : 'Imposta Livello'}
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/crm/impianti/${id}/modifica`)}
            size={isMobile ? 'small' : 'medium'}
            sx={{ flex: { xs: '1 1 0', sm: '0 0 auto' } }}
          >
            Modifica
          </Button>
        </Box>
      </Box>

      <RefillBillingSettings plantId={id}/>

      {/* Info Cards */}
      <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 2 : 3 }}>
        <Grid item xs={12} md={4}>
          <DualLevelGauge
            lastReading={forecastData?.lastReading || currentEstimateData?.lastReading}
            estimate={calculatedEstimate}
            capacity={capacity}
            threshold={threshold}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          {/* Stima Esaurimento Gas - Card Grande */}
          <Card sx={{ 
            height: '100%', 
            bgcolor: calculatedUrgency.urgency === 'critical' ? 'error.50' : 
                     calculatedUrgency.urgency === 'high' ? 'warning.50' : 
                     calculatedUrgency.urgency === 'medium' ? 'info.50' : 'success.50' 
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <TrendingDownIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Stima Esaurimento Gas
              </Typography>
              {isLoadingForecast ? (
                <LinearProgress />
              ) : forecastData?.canEstimate ? (
                <Box>
                  {/* Alert urgenza */}
                  <Alert 
                    severity={
                      calculatedUrgency.urgency === 'critical' ? 'error' :
                      calculatedUrgency.urgency === 'high' ? 'warning' :
                      calculatedUrgency.urgency === 'medium' ? 'info' : 'success'
                    }
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="body1">
                      <strong>{calculatedUrgency.message}</strong>
                    </Typography>
                  </Alert>
                  
                  {/* Timeline Orizzontale */}
                  <Box sx={{ 
                    overflowX: 'auto',
                    pb: 1,
                    mb: 2,
                    '&::-webkit-scrollbar': { height: 8 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.400', borderRadius: 4 }
                  }}>
                    {/* Calcolo progressivo dei litri stimati */}
                    {(() => {
                      const cap = parseFloat(plant?.capacita_totale) || 0;
                      const readingLiters = forecastData.lastReading?.liters || 0;
                      const readingPercent = forecastData.lastReading?.percent || 0;
                      const daysSinceReading = forecastData.lastReading?.daysSince || 0;
                      const avgDailyConsumption = forecastData.estimate?.avgDailyConsumption || 0;
                      
                      // Calcola il totale dei litri aggiunti (rifornimenti + ordini)
                      const totalDeliveries = (forecastData.deliveries || []).reduce((sum, d) => sum + (d.quantity_liters || 0), 0);
                      const totalOrders = (forecastData.pendingOrders || []).reduce((sum, o) => sum + (o.quantity_liters || 0), 0);
                      const totalAddedLiters = totalDeliveries + totalOrders;
                      
                      // Giacenza dopo rilevazione + tutti i rifornimenti/ordini
                      const giacenzaDopoAggiunte = readingLiters + totalAddedLiters;
                      
                      // Consumo stimato totale = consumo giornaliero × giorni dalla rilevazione
                      const consumoStimatoTotale = avgDailyConsumption * daysSinceReading;
                      
                      // Giacenza attuale stimata = giacenza dopo aggiunte - consumo stimato
                      const giacenzaAttualeStimata = Math.max(0, giacenzaDopoAggiunte - consumoStimatoTotale);
                      const percentualeAttualeStimata = cap > 0 ? (giacenzaAttualeStimata / cap) * 100 : 0;
                      
                      // Calcolo soglia e esaurimento dalla giacenza attuale stimata
                      const sogliaLitri = (cap * threshold) / 100;
                      const litriFinoSoglia = giacenzaAttualeStimata - sogliaLitri;
                      const giorniAlSoglia = avgDailyConsumption > 0 ? Math.max(0, Math.floor(litriFinoSoglia / avgDailyConsumption)) : 999;
                      const dataSoglia = new Date();
                      dataSoglia.setDate(dataSoglia.getDate() + giorniAlSoglia);
                      
                      const giorniAlEsaurimento = avgDailyConsumption > 0 ? Math.max(0, Math.floor(giacenzaAttualeStimata / avgDailyConsumption)) : 999;
                      const dataEsaurimento = new Date();
                      dataEsaurimento.setDate(dataEsaurimento.getDate() + giorniAlEsaurimento);
                      
                      // Data di rilevazione per calcolare i giorni
                      const readingDate = forecastData.lastReading?.date ? new Date(forecastData.lastReading.date) : new Date();
                      
                      // Unisci rifornimenti e ordini in un unico array con tipo
                      const allEvents = [
                        ...(forecastData.deliveries || []).map(d => ({
                          ...d,
                          eventType: 'delivery',
                          eventDate: new Date(d.delivery_date)
                        })),
                        ...(forecastData.pendingOrders || []).map(o => ({
                          ...o,
                          eventType: 'order',
                          eventDate: new Date(o.expected_delivery_date || o.order_date)
                        }))
                      ];
                      
                      // Ordina cronologicamente
                      allEvents.sort((a, b) => a.eventDate - b.eventDate);
                      
                      // Calcola i litri cumulativi dopo ogni evento, considerando il consumo fino a quella data
                      let cumulativeAdded = 0; // Totale litri aggiunti (rifornimenti + ordini)
                      const eventsWithCumulative = allEvents.map(event => {
                        const daysSinceReadingToEvent = Math.max(0, Math.floor((event.eventDate - readingDate) / (1000 * 60 * 60 * 24)));
                        const consumoFinoAEvent = avgDailyConsumption * daysSinceReadingToEvent;
                        cumulativeAdded += (event.quantity_liters || 0);
                        const giacenzaStimataAlEvent = Math.max(0, readingLiters + cumulativeAdded - consumoFinoAEvent);
                        return { 
                          ...event, 
                          cumulativeLiters: giacenzaStimataAlEvent,
                          cumulativePercent: cap > 0 ? (giacenzaStimataAlEvent / cap) * 100 : 0,
                          consumoStimato: consumoFinoAEvent,
                          daysSinceReading: daysSinceReadingToEvent
                        };
                      });
                      
                      // Stili uniformi
                      const iconSize = 52;
                      const boxWidth = 145;
                      const iconStyle = {
                        width: iconSize,
                        height: iconSize,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 3,
                        zIndex: 2
                      };
                      const boxStyle = {
                        mt: 1.5,
                        p: 2,
                        borderRadius: 2,
                        textAlign: 'center',
                        width: boxWidth,
                        minHeight: 130
                      };
                      // Stili tipografici uniformi per tutti i box
                      const labelStyle = { fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.5, mb: 0.5, textTransform: 'uppercase' };
                      // dateStyle e litersStyle ora prendono il colore come parametro
                      const getDateStyle = (color) => ({ fontSize: '0.95rem', fontWeight: 600, color: `${color}.dark`, mb: 0.5 });
                      const percentStyle = { fontSize: '1.8rem', fontWeight: 700, lineHeight: 1.1 };
                      const getLitersStyle = (color) => ({ fontSize: '1rem', fontWeight: 600, color: `${color}.dark`, mt: 0.5 });
                      
                      const connectorStyle = {
                        height: 4,
                        flex: '0 0 16px',
                        alignSelf: 'flex-start',
                        mt: `${iconSize/2 - 2}px`,
                        mx: -0.5
                      };
                      
                      return (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start',
                      gap: 0,
                      position: 'relative',
                      minWidth: 'max-content',
                      px: 2,
                      py: 1
                    }}>
                      
                      {/* 1. Rilevazione */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ ...iconStyle, bgcolor: 'primary.main' }}>
                          <GasIcon sx={{ color: 'white', fontSize: 28 }} />
                        </Box>
                        <Box sx={{ ...boxStyle, bgcolor: 'primary.50', border: '2px solid', borderColor: 'primary.300' }}>
                          <Typography sx={{ ...labelStyle, color: 'primary.main' }}>
                            Rilevazione
                          </Typography>
                          <Typography sx={getDateStyle('primary')}>
                            {safeFormatDate(forecastData.lastReading?.date, 'dd MMM yyyy')}
                          </Typography>
                          <Typography sx={{ ...percentStyle, color: 'primary.dark' }}>
                            {readingPercent?.toFixed(0)}%
                          </Typography>
                          <Typography sx={getLitersStyle('primary')}>
                            {Math.round(readingLiters || 0).toLocaleString('it-IT')} L
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ ...connectorStyle, bgcolor: 'grey.400' }} />
                      
                      {/* 2. Rifornimenti e Ordini in ordine cronologico */}
                      {eventsWithCumulative.map((event) => (
                        <React.Fragment key={`${event.eventType}-${event.id}`}>
                          {event.eventType === 'delivery' ? (
                            // Rifornimento
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <Box sx={{ ...iconStyle, bgcolor: 'success.main' }}>
                                <DeliveryIcon sx={{ color: 'white', fontSize: 26 }} />
                              </Box>
                              <Box sx={{ ...boxStyle, bgcolor: 'success.50', border: '2px solid', borderColor: 'success.300' }}>
                                <Typography sx={{ ...labelStyle, color: 'success.dark' }}>
                                  Rifornimento
                                </Typography>
                                <Typography sx={getDateStyle('success')}>
                                  {safeFormatDate(event.delivery_date, 'dd MMM yyyy')}
                                </Typography>
                                <Typography sx={{ ...percentStyle, color: 'success.dark' }}>
                                  {event.cumulativePercent?.toFixed(0)}%
                                </Typography>
                                <Typography sx={getLitersStyle('success')}>
                                  {Math.round(event.cumulativeLiters || 0).toLocaleString('it-IT')} L
                                </Typography>
                                {/* Info aggiuntiva */}
                                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'success.200' }}>
                                  <Typography sx={{ fontSize: '0.75rem', color: 'success.main', fontWeight: 600 }}>
                                    +{Math.round(event.quantity_liters || 0).toLocaleString('it-IT')} L
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.7rem', color: 'error.main', fontWeight: 500, mt: 0.3 }}>
                                    -{event.consumoStimato?.toLocaleString('it-IT', {maximumFractionDigits: 0})} L ({event.daysSinceReading} gg)
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          ) : (
                            // Ordine
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <Box sx={{ ...iconStyle, bgcolor: 'info.main', border: '3px dashed', borderColor: 'info.dark' }}>
                                <DeliveryIcon sx={{ color: 'white', fontSize: 26 }} />
                              </Box>
                              <Box sx={{ ...boxStyle, bgcolor: 'info.50', border: '2px dashed', borderColor: 'info.400' }}>
                                <Typography sx={{ ...labelStyle, color: 'info.dark' }}>
                                  Ordine
                                </Typography>
                                <Typography sx={getDateStyle('info')}>
                                  {safeFormatDate(event.expected_delivery_date || event.order_date, 'dd MMM yyyy')}
                                </Typography>
                                <Typography sx={{ ...percentStyle, color: 'info.dark' }}>
                                  {event.cumulativePercent?.toFixed(0)}%
                                </Typography>
                                <Typography sx={getLitersStyle('info')}>
                                  {Math.round(event.cumulativeLiters || 0).toLocaleString('it-IT')} L
                                </Typography>
                                {/* Info aggiuntiva */}
                                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed', borderColor: 'info.300' }}>
                                  <Typography sx={{ fontSize: '0.75rem', color: 'info.main', fontWeight: 600 }}>
                                    +{Math.round(event.quantity_liters || 0).toLocaleString('it-IT')} L
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.7rem', color: 'error.main', fontWeight: 500, mt: 0.3 }}>
                                    -{event.consumoStimato?.toLocaleString('it-IT', {maximumFractionDigits: 0})} L ({event.daysSinceReading} gg)
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          )}
                          <Box sx={{ ...connectorStyle, bgcolor: 'grey.400' }} />
                        </React.Fragment>
                      ))}
                      
                      {/* 3. Oggi */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ ...iconStyle, bgcolor: 'grey.700' }}>
                          <TrendingDownIcon sx={{ color: 'white', fontSize: 28 }} />
                        </Box>
                        <Box sx={{ ...boxStyle, bgcolor: 'grey.100', border: '2px solid', borderColor: 'grey.400' }}>
                          <Typography sx={{ ...labelStyle, color: 'grey.700' }}>
                            Oggi
                          </Typography>
                          <Typography sx={getDateStyle('grey')}>
                            {safeFormatDate(new Date(), 'dd MMM yyyy')}
                          </Typography>
                          <Typography sx={{ ...percentStyle, color: 'grey.800' }}>
                            {Math.max(0, percentualeAttualeStimata).toFixed(0)}%
                          </Typography>
                          <Typography sx={getLitersStyle('grey')}>
                            {Math.max(0, giacenzaAttualeStimata).toLocaleString('it-IT', {maximumFractionDigits: 0})} L
                          </Typography>
                          {/* Info aggiuntiva - consumo */}
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'grey.300' }}>
                            <Typography sx={{ fontSize: '0.7rem', color: 'grey.600', mb: 0.5 }}>
                              Consumo stimato ({daysSinceReading} gg)
                            </Typography>
                            <Typography sx={{ fontSize: '0.85rem', color: 'grey.800', fontWeight: 600 }}>
                              {consumoStimatoTotale.toLocaleString('it-IT', {maximumFractionDigits: 0})} L totali
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                              {avgDailyConsumption.toFixed(1)} L/giorno
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      
                      <Box sx={{ ...connectorStyle, bgcolor: 'transparent', borderTop: '4px dashed', borderColor: litriFinoSoglia < 0 ? 'error.400' : 'warning.400' }} />
                      
                      {/* 5. Soglia */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ ...iconStyle, bgcolor: litriFinoSoglia < 0 ? 'error.main' : 'warning.main' }}>
                          <WarningIcon sx={{ color: 'white', fontSize: 28 }} />
                        </Box>
                        <Box sx={{ ...boxStyle, bgcolor: litriFinoSoglia < 0 ? 'error.50' : 'warning.50', border: '2px solid', borderColor: litriFinoSoglia < 0 ? 'error.400' : 'warning.400' }}>
                          <Typography sx={{ ...labelStyle, color: litriFinoSoglia < 0 ? 'error.dark' : 'warning.dark' }}>
                            {litriFinoSoglia < 0 ? '⚠️ GIÀ SOTTO' : `Soglia ${threshold}%`}
                          </Typography>
                          <Typography sx={getDateStyle(litriFinoSoglia < 0 ? 'error' : 'warning')}>
                            {litriFinoSoglia < 0 ? 'SOGLIA SUPERATA!' : safeFormatDate(dataSoglia, 'dd MMM yyyy')}
                          </Typography>
                          <Typography sx={{ ...percentStyle, color: litriFinoSoglia < 0 ? 'error.dark' : 'warning.dark' }}>
                            {threshold}%
                          </Typography>
                          <Typography sx={getLitersStyle(litriFinoSoglia < 0 ? 'error' : 'warning')}>
                            {sogliaLitri.toLocaleString('it-IT', {maximumFractionDigits: 0})} L
                          </Typography>
                          {/* Info aggiuntiva */}
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: litriFinoSoglia < 0 ? 'error.300' : 'warning.300' }}>
                            <Typography sx={{ fontSize: '0.75rem', color: litriFinoSoglia < 0 ? 'error.dark' : 'warning.dark', fontWeight: 600 }}>
                              {litriFinoSoglia < 0 
                                ? `Sotto di ${Math.abs(litriFinoSoglia).toLocaleString('it-IT', {maximumFractionDigits: 0})} L`
                                : `Tra ${giorniAlSoglia} giorni`
                              }
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      
                      <Box sx={{ ...connectorStyle, bgcolor: 'transparent', borderTop: '4px dashed', borderColor: 'error.400' }} />
                      
                      {/* 6. Esaurimento */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ ...iconStyle, bgcolor: 'error.main' }}>
                          <WarningIcon sx={{ color: 'white', fontSize: 28 }} />
                        </Box>
                        <Box sx={{ ...boxStyle, bgcolor: 'error.50', border: '2px solid', borderColor: 'error.400' }}>
                          <Typography sx={{ ...labelStyle, color: 'error.dark' }}>
                            Esaurimento
                          </Typography>
                          <Typography sx={getDateStyle('error')}>
                            {safeFormatDate(dataEsaurimento, 'dd MMM yyyy')}
                          </Typography>
                          <Typography sx={{ ...percentStyle, color: 'error.dark' }}>
                            0%
                          </Typography>
                          <Typography sx={getLitersStyle('error')}>
                            0 L
                          </Typography>
                          {/* Info aggiuntiva */}
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'error.300' }}>
                            <Typography sx={{ fontSize: '0.75rem', color: 'error.dark', fontWeight: 600 }}>
                              Tra {giorniAlEsaurimento} giorni
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      
                    </Box>
                      );
                    })()}
                  </Box>

                  {/* Accordion con storico consumi usato per il calcolo */}
                  <Accordion sx={{ bgcolor: 'background.default', mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                          📊 Storico Consumi Stagionali
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, ml: 'auto', alignItems: 'center' }}>
                          <Box sx={{ minWidth: 80, textAlign: 'right' }}>
                            <Typography variant="body2" color="text.secondary">
                              {forecastData.historicalData?.length || 0} anni
                            </Typography>
                          </Box>
                          <Box sx={{ minWidth: 120, textAlign: 'right' }}>
                            <Typography variant="body2" color="info.main" fontWeight="medium">
                              {forecastData.methodology?.periodStart} - {forecastData.methodology?.periodEnd}
                            </Typography>
                          </Box>
                          <Box sx={{ minWidth: 100, textAlign: 'right' }}>
                            <Typography variant="body2" color="primary.main" fontWeight="bold">
                              {forecastData.estimate?.avgDailyConsumption?.toFixed(1) || 0} L/g
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {forecastData.methodology?.extendedSearch && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          {forecastData.methodology.extendedSearchWarning || 
                           `Ricerca estesa: non trovati dati negli ultimi 3 anni, ricerca estesa a ${forecastData.methodology.yearsSearched || 5} anni`}
                        </Alert>
                      )}
                      
                      {forecastData.historicalData?.length > 0 ? (
                        <Box>
                          {forecastData.historicalData.map((row, idx) => (
                            <Box 
                              key={idx}
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                p: 1.5, 
                                borderBottom: idx < forecastData.historicalData.length - 1 ? '1px solid' : 'none',
                                borderColor: 'divider',
                                '&:hover': { bgcolor: 'action.hover' }
                              }}
                            >
                              <Typography variant="h6" sx={{ fontWeight: 'bold', minWidth: 60 }}>
                                {row.year}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100, ml: 2 }}>
                                {safeFormatDate(row.startDate, 'dd/MM')} - {safeFormatDate(row.endDate, 'dd/MM')}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 2, ml: 'auto', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 70, textAlign: 'right' }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {row.days || 0} gg
                                  </Typography>
                                </Box>
                                <Box sx={{ minWidth: 100, textAlign: 'right' }}>
                                  <Typography variant="body2" color="info.main" fontWeight="medium">
                                    {row.liters?.toLocaleString('it-IT', { maximumFractionDigits: 0 })} L
                                  </Typography>
                                </Box>
                                <Box sx={{ minWidth: 80, textAlign: 'right' }}>
                                  <Typography variant="body2" color="primary.main" fontWeight="bold">
                                    {row.dailyAverage?.toFixed(1) || 0} L/g
                                  </Typography>
                                </Box>
                                <Box sx={{ minWidth: 60, textAlign: 'right' }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {row.invoiceCount || 0} fatt.
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          ))}
                          {/* Riga totale/media */}
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              p: 1.5, 
                              bgcolor: 'grey.100',
                              borderRadius: 1,
                              mt: 1
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              Media calcolata
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, ml: 'auto', alignItems: 'center' }}>
                              <Box sx={{ minWidth: 180, textAlign: 'right' }}>
                                <Typography variant="body2" color="text.secondary">
                                  Base per stima esaurimento:
                                </Typography>
                              </Box>
                              <Box sx={{ minWidth: 80, textAlign: 'right' }}>
                                <Typography variant="body1" color="primary.main" fontWeight="bold">
                                  {forecastData.estimate?.avgDailyConsumption?.toFixed(1) || 0} L/giorno
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Nessun dato storico disponibile per questo periodo
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Box>
              ) : (
                <Alert severity="warning">
                  {forecastData?.reason || 'Dati insufficienti per calcolare la stima. Servono consumi storici dello stesso periodo stagionale.'}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <PlantAiSummaryPanel key={id} plantId={id} />

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant={isMobile ? 'scrollable' : 'standard'} scrollButtons={isMobile ? 'auto' : false} allowScrollButtonsMobile>
          <Tab icon={<MaintenanceIcon />} label="Interventi" />
          <Tab icon={<VVFIcon />} label="Pratica VVF" />
          <Tab icon={<MeterIcon />} label="Contatori" />
          <Tab icon={<HistoryIcon />} label="Storico Livelli" />
          <Tab icon={<ReceiptIcon />} label="Consumi" />
          <Tab icon={<DeliveryIcon />} label="Rifornimenti" />
          <Tab icon={<BalanceIcon />} label="Controllo Litri" />
          <Tab icon={<PhotoLibraryIcon />} label="Foto PWA" />
        </Tabs>
      </Paper>

      {/* Tab Interventi */}
      <TabPanel value={tabValue} index={0}>
        {/* Sezione Note Intervento */}
        <PlantInterventionNotesTab plantId={id} metersData={metersData} />

        <Divider sx={{ my: 4 }} />

        {/* Sezione Storico Manutenzioni */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6">Storico Manutenzioni</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setMaintenanceDialog(true)}
            size={isMobile ? 'small' : 'medium'}
            fullWidth={isMobile}
          >
            {isMobile ? 'Nuova' : 'Nuova Manutenzione'}
          </Button>
        </Box>
        {isMobile ? (
          <MobileCardList
            rows={plant.maintenances || []}
            emptyMessage="Nessuna manutenzione registrata"
            primaryField="tipo"
            secondaryField="descrizione"
            statusField={{
              field: 'esito',
              colorMap: {
                positivo: 'success',
                negativo: 'error',
                sospeso: 'warning',
              },
            }}
            extraFields={[
              {
                field: 'data_manutenzione',
                label: 'Data',
                render: (row) => row.data_manutenzione ? format(new Date(row.data_manutenzione), 'dd/MM/yyyy', { locale: it }) : '-',
              },
              {
                field: 'tecnico',
                label: 'Tecnico',
                render: (row) => row.tecnico || '-',
              },
              {
                field: 'data_prossima',
                label: 'Prossima',
                render: (row) => row.data_prossima ? format(new Date(row.data_prossima), 'dd/MM/yyyy', { locale: it }) : '-',
              },
            ]}
            actions={(row) => (
              <IconButton
                size="small"
                color="error"
                onClick={() => deleteMaintenanceMutation.mutate(row.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          />
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Tecnico</TableCell>
                  <TableCell>Descrizione</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Esito</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Prossima</TableCell>
                  <TableCell>Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plant.maintenances?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Nessuna manutenzione registrata
                    </TableCell>
                  </TableRow>
                ) : (
                  plant.maintenances?.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell>
                        {m.data_manutenzione && format(new Date(m.data_manutenzione), 'dd/MM/yyyy', { locale: it })}
                      </TableCell>
                      <TableCell>
                        <Chip label={m.tipo} size="small" />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{m.tecnico || '-'}</TableCell>
                      <TableCell>{m.descrizione || '-'}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Chip
                          label={m.esito}
                          size="small"
                          color={m.esito === 'positivo' ? 'success' : m.esito === 'negativo' ? 'error' : 'warning'}
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {m.data_prossima && format(new Date(m.data_prossima), 'dd/MM/yyyy', { locale: it })}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => deleteMaintenanceMutation.mutate(m.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Tab Pratica VVF */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Pratiche VVF</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setVvfDialog(true)}
          >
            Nuova Pratica
          </Button>
        </Box>
        {plant.vvfPractices?.length === 0 ? (
          <Alert severity="info">Nessuna pratica VVF registrata per questo impianto</Alert>
        ) : (
          <Grid container spacing={2}>
            {plant.vvfPractices?.map((p) => (
              <Grid item xs={12} md={6} key={p.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">
                        {p.numero_pratica || 'Pratica ' + p.id}
                      </Typography>
                      <Chip
                        label={p.stato?.replace(/_/g, ' ')}
                        color={
                          p.stato === 'rilasciato_cpi' ? 'success' :
                          p.stato === 'scaduta' ? 'error' :
                          p.stato === 'in_istruttoria' ? 'warning' : 'default'
                        }
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Tipo: {p.tipo_pratica?.replace(/_/g, ' ')}
                    </Typography>
                    {p.comando_vvf && (
                      <Typography variant="body2">Comando: {p.comando_vvf}</Typography>
                    )}
                    {p.data_scadenza && (
                      <Typography variant="body2" color={new Date(p.data_scadenza) < new Date() ? 'error' : 'inherit'}>
                        Scadenza: {safeFormatDate(p.data_scadenza)}
                      </Typography>
                    )}
                    {p.professionista && (
                      <Typography variant="body2">Professionista: {p.professionista}</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <Divider sx={{ my: 4 }} />

        {/* Sezione Documenti */}
        <PlantDocumentsSection plantId={id} excludePwaGroups />
      </TabPanel>

      {/* Tab Contatori */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6">Contatori Collegati all'Impianto</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={`${metersData?.length || 0} contatori`}
              color="primary"
              variant="outlined"
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<EuroIcon />}
              onClick={handleOpenPriceDialog}
              disabled={!metersData?.length}
            >
              Aggiorna prezzi
            </Button>
          </Box>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {[
                  { id: 'sotto_zona', label: 'Progr.', align: 'left', hideOnMobile: false },
                  { id: 'meter_number', label: 'N° Contatore', align: 'left', hideOnMobile: false },
                  { id: 'c_nome', label: 'Intestatario', align: 'left', hideOnMobile: false },
                  { id: 'c_localita', label: 'Località', align: 'left', hideOnMobile: true },
                  { id: 'ultima_lettura', label: 'Ultima Lettura', align: 'right', hideOnMobile: true },
                  { id: 'data_ultima_lettura', label: 'Data Lettura', align: 'left', hideOnMobile: true },
                  { id: 'consumo_reale', label: 'Consumo Reale', align: 'right', hideOnMobile: true },
                  { id: 'prezzo_litro', label: '€/Litro', align: 'right', hideOnMobile: true },
                  { id: 'stato', label: 'Stato', align: 'left', hideOnMobile: false },
                ].map((col) => (
                  <TableCell
                    key={col.id}
                    align={col.align}
                    sortDirection={metersOrderBy === col.id ? metersOrder : false}
                    sx={col.hideOnMobile ? { display: { xs: 'none', md: 'table-cell' } } : {}}
                  >
                    <TableSortLabel
                      active={metersOrderBy === col.id}
                      direction={metersOrderBy === col.id ? metersOrder : 'asc'}
                      onClick={() => {
                        const isAsc = metersOrderBy === col.id && metersOrder === 'asc';
                        setMetersOrder(isAsc ? 'desc' : 'asc');
                        setMetersOrderBy(col.id);
                      }}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {metersData?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Nessun contatore associato a questo impianto
                  </TableCell>
                </TableRow>
              ) : (
                sortedMeters.map((m) => (
                  <TableRow key={m.id} hover>
                    <TableCell>
                      {m.sotto_zona || m.c_sotto_zo || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip label={m.meter_number} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" color="action" />
                        {m.c_nome || m.intestatario || m.customer?.ragione_sociale || '-'}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{m.c_localita || m.localita || '-'}</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography variant="body2" fontWeight="medium">
                        {m.ultima_lettura ? parseFloat(m.ultima_lettura).toLocaleString('it-IT', { minimumFractionDigits: 2 }) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {m.data_ultima_lettura ? format(new Date(m.data_ultima_lettura), 'dd/MM/yyyy', { locale: it }) : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {m.consumo_reale ? parseFloat(m.consumo_reale).toLocaleString('it-IT', { minimumFractionDigits: 2 }) + ' mc' : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {m.prezzo_litro ? `€ ${parseFloat(m.prezzo_litro).toFixed(4)}` : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={m.stato || 'attivo'} 
                        size="small" 
                        color={m.stato === 'attivo' ? 'success' : m.stato === 'sospeso' ? 'warning' : m.stato === 'cessato' ? 'default' : 'error'}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Riepilogo statistiche contatori */}
        {metersData?.length > 0 && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Riepilogo</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">Totale Contatori</Typography>
                <Typography variant="h6">{metersData.length}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">Attivi</Typography>
                <Typography variant="h6" color="success.main">
                  {metersData.filter(m => m.stato === 'attivo' || !m.stato).length}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">Consumo Totale</Typography>
                <Typography variant="h6">
                  {metersData.reduce((sum, m) => sum + (parseFloat(m.consumo_reale) || 0), 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })} mc
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">Media €/Litro</Typography>
                <Typography variant="h6">
                  € {(metersData.filter(m => m.prezzo_litro).reduce((sum, m) => sum + parseFloat(m.prezzo_litro), 0) / metersData.filter(m => m.prezzo_litro).length || 0).toFixed(4)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
      </TabPanel>

      {/* Tab Storico Livelli */}
      <TabPanel value={tabValue} index={3}>
        {/* Info Livello Iniziale (solo visualizzazione) */}
        {levelInfoData?.initialLevel && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Livello iniziale impostato:</strong> {levelInfoData.initialLevel.percent}% 
            ({levelInfoData.initialLevel.liters?.toFixed(0)} L) 
            dal {safeFormatDate(levelInfoData.initialLevel.date)}
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              Per modificare il livello iniziale, vai su "Modifica Impianto"
            </Typography>
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Sezione Rilevazioni Livelli */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Rilevazioni Livelli
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setReadingDialog(true)}
              >
                Nuova Rilevazione
              </Button>
            </Box>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Tecnico</TableCell>
                    <TableCell align="right">Rilevato</TableCell>
                    <TableCell align="right">Calcolato</TableCell>
                    <TableCell align="right">Scostamento</TableCell>
                    <TableCell align="center" width={50}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {readingsData?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">
                          Nessuna rilevazione registrata
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    readingsData?.map((r) => (
                      <TableRow 
                        key={r.id}
                        sx={{ 
                          bgcolor: r.is_anomaly ? 'error.50' : 'inherit',
                          '&:hover': { bgcolor: r.is_anomaly ? 'error.100' : 'action.hover' }
                        }}
                      >
                        <TableCell>
                          {r.created_at && format(new Date(r.created_at), 'dd/MM/yyyy', { locale: it })}
                        </TableCell>
                        <TableCell>{r.technician || '-'}</TableCell>
                        <TableCell align="right">
                          {parseFloat(r.new_liters || 0).toFixed(0)} L
                          <Typography variant="caption" display="block" color="text.secondary">
                            ({parseFloat(r.new_percent || 0).toFixed(0)}%)
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {parseFloat(r.calculated_liters || 0).toFixed(0)} L
                        </TableCell>
                        <TableCell align="right">
                          {r.is_anomaly ? (
                            <Chip 
                              size="small" 
                              icon={<WarningIcon />} 
                              label={`${parseFloat(r.deviation_liters || 0) > 0 ? '+' : ''}${parseFloat(r.deviation_liters || 0).toFixed(0)} L`}
                              color="error"
                            />
                          ) : (
                            <Typography 
                              color={parseFloat(r.deviation_liters || 0) === 0 ? 'text.secondary' : 'warning.main'}
                            >
                              {parseFloat(r.deviation_liters || 0) > 0 ? '+' : ''}{parseFloat(r.deviation_liters || 0).toFixed(0)} L
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              if (window.confirm('Eliminare questa rilevazione?')) {
                                deleteReadingMutation.mutate(r.id);
                              }
                            }}
                            disabled={deleteReadingMutation.isPending}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Le rilevazioni sono controlli effettuati dai tecnici per verificare il livello reale rispetto a quello calcolato.
              Anomalie significative possono indicare perdite o consumi non registrati.
            </Typography>
          </Grid>

          {/* Ordini in corso */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ mb: 2 }}>Ordini in Corso</Typography>
            {forecastData?.pendingOrders?.length > 0 ? (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Consegna Prevista</TableCell>
                      <TableCell>Codice</TableCell>
                      <TableCell align="right">Quantità</TableCell>
                      <TableCell>Stato</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {forecastData.pendingOrders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>
                          {safeFormatDate(order.expected_delivery_date || order.order_date, 'dd/MM/yyyy')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={order.order_code || `#${order.id}`}
                            color="info"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>
                          +{Math.round(order.quantity_liters || 0).toLocaleString('it-IT')} L
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={order.status === 'pending' ? 'In attesa' : order.status === 'confirmed' ? 'Confermato' : order.status || 'In corso'}
                            color={order.status === 'confirmed' ? 'success' : 'warning'}
                            variant="filled"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info" icon={<DeliveryIcon />}>
                Nessun ordine in corso per questo impianto
              </Alert>
            )}
          </Grid>
        </Grid>

        {/* Sezione Ultimi Rifornimenti - ora full width sotto */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Ultimi Rifornimenti</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>DDT</TableCell>
                  <TableCell align="right">Quantità</TableCell>
                  <TableCell align="right">Importo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deliveriesData?.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>
                      {d.delivery_date && format(new Date(d.delivery_date), 'dd/MM/yyyy', { locale: it })}
                    </TableCell>
                    <TableCell>{d.ddt_number || '-'}</TableCell>
                    <TableCell align="right">{parseFloat(d.quantity_liters).toFixed(0)} L</TableCell>
                    <TableCell align="right">
                      {d.total_amount ? `€ ${parseFloat(d.total_amount).toFixed(2)}` : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </TabPanel>

      {/* Tab Consumi */}
      <TabPanel value={tabValue} index={4}>
        {/* Filtri data */}
        <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Data inizio"
            type="date"
            size="small"
            value={consumptionStartDate}
            onChange={(e) => setConsumptionStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <TextField
            label="Data fine"
            type="date"
            size="small"
            value={consumptionEndDate}
            onChange={(e) => setConsumptionEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          {(consumptionStartDate || consumptionEndDate) && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => { setConsumptionStartDate(''); setConsumptionEndDate(''); }}
            >
              Reset
            </Button>
          )}
          <Box sx={{ ml: 'auto' }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<PdfIcon />}
              onClick={async () => {
                try {
                  const params = {};
                  if (consumptionStartDate) params.startDate = consumptionStartDate;
                  if (consumptionEndDate) params.endDate = consumptionEndDate;
                  const response = await plantsApi.getConsumptionPdf(id, params);
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `consumi_${plantData?.plant_code || id}.pdf`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                } catch (e) {
                  console.error('Errore export PDF consumi:', e);
                }
              }}
            >
              Esporta PDF
            </Button>
          </Box>
        </Paper>
        {isLoadingConsumption ? (
          <LinearProgress />
        ) : (
          <Box>
            {/* Riepilogo totale */}
            {consumptionData?.totals && consumptionData.totals.invoiceCount > 0 && (
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.50' }}>
                <Typography variant="h6" gutterBottom>
                  <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Riepilogo Totale Consumi
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Fatture</Typography>
                      <Typography variant="h4">{consumptionData.totals.invoiceCount}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <LitersIcon color="info" sx={{ fontSize: 24 }} />
                      <Typography variant="body2" color="text.secondary">Consumo Totale</Typography>
                      <Typography variant="h5" color="info.main">
                        {consumptionData.totals.liters?.toLocaleString('it-IT', { maximumFractionDigits: 0 })} L
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <EuroIcon color="primary" sx={{ fontSize: 24 }} />
                      <Typography variant="body2" color="text.secondary">Fatturato Totale</Typography>
                      <Typography variant="h5" color="primary.main">
                        € {consumptionData.totals.amount?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Da Incassare</Typography>
                      <Typography variant="h5" color={consumptionData.totals.unpaid > 0 ? 'error.main' : 'success.main'}>
                        € {consumptionData.totals.unpaid?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Accordion per anno */}
            {consumptionData?.years?.length === 0 ? (
              <Alert severity="info">Nessuna fattura trovata per i contatori di questo impianto</Alert>
            ) : (
              consumptionData?.years?.map((yearData) => (
                <Accordion key={yearData.year} defaultExpanded={false}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', minWidth: 80 }}>
                        {yearData.year}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, ml: 'auto', alignItems: 'center' }}>
                        <Box sx={{ minWidth: 90, textAlign: 'right' }}>
                          <Typography variant="body2" color="text.secondary">
                            {yearData.totals.invoiceCount} fatt.
                          </Typography>
                        </Box>
                        <Box sx={{ minWidth: 100, textAlign: 'right' }}>
                          <Typography variant="body2" color="info.main" fontWeight="medium">
                            {yearData.totals.liters?.toLocaleString('it-IT', { maximumFractionDigits: 0 })} L
                          </Typography>
                        </Box>
                        <Box sx={{ minWidth: 120, textAlign: 'right' }}>
                          <Typography variant="body2" color="primary.main" fontWeight="bold">
                            € {yearData.totals.amount?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                        <Box sx={{ minWidth: 120, textAlign: 'right' }}>
                          {yearData.totals.unpaid > 0 ? (
                            <Typography variant="body2" color="error.main">
                              - € {yearData.totals.unpaid?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="success.main">
                              Saldato
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {/* Accordion per mese */}
                    {yearData.months.map((monthData) => (
                      <Accordion key={`${yearData.year}-${monthData.month}`} sx={{ mb: 1 }} defaultExpanded={false}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                            <Typography sx={{ textTransform: 'capitalize', fontWeight: 500, minWidth: 120 }}>
                              {monthData.monthName}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, ml: 'auto', alignItems: 'center' }}>
                              <Box sx={{ minWidth: 70, textAlign: 'right' }}>
                                <Typography variant="body2" color="text.secondary">
                                  {monthData.totals.invoiceCount} fatt.
                                </Typography>
                              </Box>
                              <Box sx={{ minWidth: 90, textAlign: 'right' }}>
                                <Typography variant="body2" color="info.main">
                                  {monthData.totals.liters?.toLocaleString('it-IT', { maximumFractionDigits: 0 })} L
                                </Typography>
                              </Box>
                              <Box sx={{ minWidth: 100, textAlign: 'right' }}>
                                <Typography variant="body2" color="primary.main" fontWeight="bold">
                                  € {monthData.totals.amount?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                                </Typography>
                              </Box>
                              <Box sx={{ minWidth: 100, textAlign: 'right' }}>
                                {monthData.totals.unpaid > 0 ? (
                                  <Typography variant="body2" color="error.main">
                                    - € {monthData.totals.unpaid?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="success.main">
                                    Saldato
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>N° Fattura</TableCell>
                                  <TableCell>Cliente</TableCell>
                                  <TableCell>Contatore</TableCell>
                                  <TableCell>Data</TableCell>
                                  <TableCell>Periodo</TableCell>
                                  <TableCell align="right">Litri</TableCell>
                                  <TableCell align="right">Importo</TableCell>
                                  <TableCell align="right">Pagato</TableCell>
                                  <TableCell>Stato</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {monthData.invoices.map((inv) => (
                                  <TableRow 
                                    key={inv.id} 
                                    hover
                                    sx={{ 
                                      cursor: 'pointer',
                                      '&:hover': { bgcolor: 'action.hover' }
                                    }}
                                    onClick={() => navigate(`/crm/fatture/${inv.id}`)}
                                  >
                                    <TableCell>
                                      <Chip label={formatNuf(inv.sezionale, inv.nuf)} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <PersonIcon fontSize="small" color="action" />
                                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                          {inv.customer}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell>{inv.meterNumber || '-'}</TableCell>
                                    <TableCell>
                                      {inv.emissionDate && format(new Date(inv.emissionDate), 'dd/MM/yy', { locale: it })}
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
                                        {inv.periodRef || '-'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2" color="info.main">
                                        {inv.liters?.toLocaleString('it-IT', { maximumFractionDigits: 0 })}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2" fontWeight="medium">
                                        € {inv.amount?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2" color="success.main">
                                        € {inv.paid?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      {inv.unpaid <= 0 ? (
                                        <Chip 
                                          icon={<PaidIcon />} 
                                          label="Pagata" 
                                          size="small" 
                                          color="success" 
                                        />
                                      ) : (
                                        <Tooltip title={`Da pagare: € ${inv.unpaid?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}`}>
                                          <Chip 
                                            icon={<UnpaidIcon />} 
                                            label={inv.stato === 'parziale' ? 'Parziale' : 'Non pagata'} 
                                            size="small" 
                                            color={inv.stato === 'parziale' ? 'warning' : 'error'} 
                                          />
                                        </Tooltip>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>
        )}
      </TabPanel>

      {/* Tab Rifornimenti */}
      <TabPanel value={tabValue} index={5}>
        {/* Filtri data */}
        <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Data inizio"
            type="date"
            size="small"
            value={deliveriesStartDate}
            onChange={(e) => setDeliveriesStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <TextField
            label="Data fine"
            type="date"
            size="small"
            value={deliveriesEndDate}
            onChange={(e) => setDeliveriesEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          {(deliveriesStartDate || deliveriesEndDate) && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => { setDeliveriesStartDate(''); setDeliveriesEndDate(''); }}
            >
              Reset
            </Button>
          )}
          <Box sx={{ ml: 'auto' }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<PdfIcon />}
              onClick={async () => {
                try {
                  const params = {};
                  if (deliveriesStartDate) params.startDate = deliveriesStartDate;
                  if (deliveriesEndDate) params.endDate = deliveriesEndDate;
                  const response = await plantsApi.getDeliveriesGroupedPdf(id, params);
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `rifornimenti_${plantData?.plant_code || id}.pdf`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                } catch (e) {
                  console.error('Errore export PDF rifornimenti:', e);
                }
              }}
            >
              Esporta PDF
            </Button>
          </Box>
        </Paper>
        {isLoadingDeliveriesGrouped ? (
          <LinearProgress />
        ) : (
          <Box>
            {/* Riepilogo totale */}
            {deliveriesGroupedData?.totals && deliveriesGroupedData.totals.deliveryCount > 0 && (
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'success.50' }}>
                <Typography variant="h6" gutterBottom>
                  <DeliveryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Riepilogo Totale Rifornimenti
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={6} sm={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">DDT</Typography>
                      <Typography variant="h4">{deliveriesGroupedData.totals.deliveryCount}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <LitersIcon color="info" sx={{ fontSize: 24 }} />
                      <Typography variant="body2" color="text.secondary">Litri Totali</Typography>
                      <Typography variant="h5" color="info.main">
                        {deliveriesGroupedData.totals.liters?.toLocaleString('it-IT', { maximumFractionDigits: 0 })} L
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <EuroIcon color="primary" sx={{ fontSize: 24 }} />
                      <Typography variant="body2" color="text.secondary">Importo Totale</Typography>
                      <Typography variant="h5" color="primary.main">
                        € {deliveriesGroupedData.totals.amount?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Accordion per anno */}
            {deliveriesGroupedData?.years?.length === 0 ? (
              <Alert severity="info">Nessun rifornimento trovato per questo impianto</Alert>
            ) : (
              deliveriesGroupedData?.years?.map((yearData) => (
                <Accordion key={yearData.year} defaultExpanded={false}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', minWidth: 80 }}>
                        {yearData.year}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, ml: 'auto', alignItems: 'center' }}>
                        <Box sx={{ minWidth: 90, textAlign: 'right' }}>
                          <Typography variant="body2" color="text.secondary">
                            {yearData.totals.deliveryCount} DDT
                          </Typography>
                        </Box>
                        <Box sx={{ minWidth: 100, textAlign: 'right' }}>
                          <Typography variant="body2" color="info.main" fontWeight="medium">
                            {yearData.totals.liters?.toLocaleString('it-IT', { maximumFractionDigits: 0 })} L
                          </Typography>
                        </Box>
                        <Box sx={{ minWidth: 120, textAlign: 'right' }}>
                          <Typography variant="body2" color="primary.main" fontWeight="bold">
                            € {yearData.totals.amount?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {/* Accordion per mese */}
                    {yearData.months.map((monthData) => (
                      <Accordion key={`${yearData.year}-${monthData.month}`} sx={{ mb: 1 }} defaultExpanded={false}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                            <Typography sx={{ textTransform: 'capitalize', fontWeight: 500, minWidth: 120 }}>
                              {monthData.monthName}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, ml: 'auto', alignItems: 'center' }}>
                              <Box sx={{ minWidth: 70, textAlign: 'right' }}>
                                <Typography variant="body2" color="text.secondary">
                                  {monthData.totals.deliveryCount} DDT
                                </Typography>
                              </Box>
                              <Box sx={{ minWidth: 90, textAlign: 'right' }}>
                                <Typography variant="body2" color="info.main">
                                  {monthData.totals.liters?.toLocaleString('it-IT', { maximumFractionDigits: 0 })} L
                                </Typography>
                              </Box>
                              <Box sx={{ minWidth: 100, textAlign: 'right' }}>
                                <Typography variant="body2" color="primary.main" fontWeight="bold">
                                  € {monthData.totals.amount?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>N° DDT</TableCell>
                                  <TableCell>Data DDT</TableCell>
                                  <TableCell>Data Consegna</TableCell>
                                  <TableCell>Fornitore</TableCell>
                                  <TableCell align="right">Litri</TableCell>
                                  <TableCell align="right">€/L</TableCell>
                                  <TableCell align="right">Imponibile</TableCell>
                                  <TableCell align="right">Totale</TableCell>
                                  <TableCell>Tipo</TableCell>
                                  <TableCell>Origine</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {monthData.deliveries.map((del) => (
                                  <TableRow 
                                    key={del.id} 
                                    hover
                                  >
                                    <TableCell>
                                      <Chip label={del.ddt_number || del.delivery_code || '-'} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>
                                      {del.ddt_date && format(new Date(del.ddt_date), 'dd/MM/yy', { locale: it })}
                                    </TableCell>
                                    <TableCell>
                                      {del.delivery_date && format(new Date(del.delivery_date), 'dd/MM/yy', { locale: it })}
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                        {del.supplier || '-'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2" color="info.main">
                                        {del.quantity_liters?.toLocaleString('it-IT', { maximumFractionDigits: 0 })}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2">
                                        {del.price_per_liter ? `€ ${del.price_per_liter.toLocaleString('it-IT', { minimumFractionDigits: 4 })}` : '-'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2">
                                        {del.taxable_amount ? `€ ${del.taxable_amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}` : '-'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2" fontWeight="medium">
                                        {del.total_amount ? `€ ${del.total_amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}` : '-'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip 
                                        label={del.delivery_type === 'refill' ? 'Rifornimento' : del.delivery_type === 'partial' ? 'Parziale' : del.delivery_type === 'full' ? 'Completo' : del.delivery_type === 'emergency' ? 'Emergenza' : del.delivery_type || '-'}
                                        size="small"
                                        color={del.delivery_type === 'emergency' ? 'error' : 'default'}
                                        variant="outlined"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Chip 
                                        label={del.source === 'xml_import' ? 'XML' : del.source === 'manual' ? 'Manuale' : del.source === 'order' ? 'Ordine' : del.source || '-'}
                                        size="small"
                                        variant="outlined"
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>
        )}
      </TabPanel>

      {/* Tab Controllo Litri */}
      <TabPanel value={tabValue} index={6}>
        <LitersControlTimeline plantId={id} />
      </TabPanel>

      {/* Tab Foto PWA */}
      <TabPanel value={tabValue} index={7}>
        <PlantPwaPhotosTab plantId={id} plantLatitude={plantData?.latitude} plantLongitude={plantData?.longitude} />
      </TabPanel>



      {/* Dialog Aggiornamento Prezzi Contatori */}
      <Dialog open={priceDialogOpen} onClose={handleClosePriceDialog} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EuroIcon color="primary" />
          Aggiorna prezzi contatori
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5, mb: 2 }}>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Nuovo prezzo €/litro"
                value={bulkPrice}
                onChange={(event) => setBulkPrice(event.target.value)}
                inputProps={{ inputMode: 'decimal' }}
                error={Boolean(bulkPrice && bulkPriceValidation.error)}
                helperText={
                  bulkPrice
                    ? bulkPriceValidation.error || `Anteprima: ${formatMeterPrice(bulkPriceValidation.value)}`
                    : 'Inserisci il prezzo da applicare ai contatori selezionati'
                }
                autoFocus
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Alert severity="info" variant="outlined">
                Selezionati {selectedMeterIds.length} contatori su {sortedMeters.length}. Verrà modificato solo il prezzo dei contatori spuntati.
              </Alert>
            </Grid>
          </Grid>

          <TableContainer component={Paper} sx={{ maxHeight: { xs: '55vh', md: 460 } }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allMetersSelected}
                      indeterminate={someMetersSelected}
                      onChange={(event) => handleToggleAllMeters(event.target.checked)}
                      disabled={sortedMeters.length === 0 || bulkPriceMutation.isPending}
                    />
                  </TableCell>
                  <TableCell>Contatore</TableCell>
                  <TableCell>Intestatario</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Località</TableCell>
                  <TableCell align="right">Prezzo attuale</TableCell>
                  <TableCell align="right">Nuovo prezzo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedMeters.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">Nessun contatore associato a questo impianto</TableCell>
                  </TableRow>
                ) : sortedMeters.map((meter) => {
                  const selected = selectedMeterIds.includes(meter.id);
                  return (
                    <TableRow
                      key={meter.id}
                      hover
                      selected={selected}
                      onClick={() => !bulkPriceMutation.isPending && handleToggleMeter(meter.id)}
                      sx={{ cursor: bulkPriceMutation.isPending ? 'default' : 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected}
                          onChange={() => handleToggleMeter(meter.id)}
                          onClick={(event) => event.stopPropagation()}
                          disabled={bulkPriceMutation.isPending}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label={meter.meter_number || meter.id} size="small" color="primary" variant="outlined" />
                          <Typography variant="caption" color="text.secondary">
                            {meter.sotto_zona || meter.c_sotto_zo || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{getMeterDisplayName(meter)}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{getMeterDisplayPlace(meter)}</TableCell>
                      <TableCell align="right">{formatMeterPrice(meter.prezzo_litro)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={selected && bulkPriceValidation.value ? 'bold' : 'normal'} color={selected && bulkPriceValidation.value ? 'primary.main' : 'text.secondary'}>
                          {selected && bulkPriceValidation.value ? formatMeterPrice(bulkPriceValidation.value) : '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            {selectedMeterIds.length} selezionati
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleClosePriceDialog} disabled={bulkPriceMutation.isPending}>Annulla</Button>
            <Button
              variant="contained"
              startIcon={<EuroIcon />}
              onClick={handleApplyBulkPrice}
              disabled={bulkPriceMutation.isPending || selectedMeterIds.length === 0 || !bulkPriceValidation.value}
            >
              {bulkPriceMutation.isPending ? 'Aggiornamento...' : 'Applica prezzo'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={priceSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setPriceSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={priceSnackbar.severity}
          variant="filled"
          onClose={() => setPriceSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {priceSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Dialog Imposta Livello */}
      <Dialog open={levelDialog} onClose={() => setLevelDialog(false)} fullScreen={isMobile}>
        <DialogTitle>Imposta Livello Manualmente</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Litri"
            type="number"
            value={newLevel.liters}
            onChange={(e) => setNewLevel({ ...newLevel, liters: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
            helperText={`Capacità totale: ${capacity} L`}
          />
          <TextField
            fullWidth
            label="Motivo (opzionale)"
            value={newLevel.reason}
            onChange={(e) => setNewLevel({ ...newLevel, reason: e.target.value })}
            placeholder="Es: Lettura manuale contatore"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLevelDialog(false)}>Annulla</Button>
          <Button
            variant="contained"
            onClick={() => setLevelMutation.mutate(newLevel)}
            disabled={!newLevel.liters || setLevelMutation.isPending}
          >
            Conferma
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Nuova Manutenzione */}
      <Dialog open={maintenanceDialog} onClose={() => setMaintenanceDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Registra Manutenzione</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={newMaintenance.tipo}
                  label="Tipo"
                  onChange={(e) => setNewMaintenance({ ...newMaintenance, tipo: e.target.value })}
                >
                  <MenuItem value="ordinaria">Ordinaria</MenuItem>
                  <MenuItem value="straordinaria">Straordinaria</MenuItem>
                  <MenuItem value="verifica_periodica">Verifica Periodica</MenuItem>
                  <MenuItem value="riparazione">Riparazione</MenuItem>
                  <MenuItem value="sostituzione">Sostituzione</MenuItem>
                  <MenuItem value="collaudo">Collaudo</MenuItem>
                  <MenuItem value="ispezione">Ispezione</MenuItem>
                  <MenuItem value="altro">Altro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Data"
                type="date"
                value={newMaintenance.data_manutenzione}
                onChange={(e) => setNewMaintenance({ ...newMaintenance, data_manutenzione: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tecnico/Ditta"
                value={newMaintenance.tecnico}
                onChange={(e) => setNewMaintenance({ ...newMaintenance, tecnico: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrizione"
                multiline
                rows={3}
                value={newMaintenance.descrizione}
                onChange={(e) => setNewMaintenance({ ...newMaintenance, descrizione: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Esito</InputLabel>
                <Select
                  value={newMaintenance.esito}
                  label="Esito"
                  onChange={(e) => setNewMaintenance({ ...newMaintenance, esito: e.target.value })}
                >
                  <MenuItem value="positivo">Positivo</MenuItem>
                  <MenuItem value="negativo">Negativo</MenuItem>
                  <MenuItem value="in_sospeso">In Sospeso</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Data Prossima"
                type="date"
                value={newMaintenance.data_prossima || ''}
                onChange={(e) => setNewMaintenance({ ...newMaintenance, data_prossima: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMaintenanceDialog(false)}>Annulla</Button>
          <Button
            variant="contained"
            onClick={() => createMaintenanceMutation.mutate(newMaintenance)}
            disabled={createMaintenanceMutation.isPending}
          >
            Registra
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Nuova Pratica VVF */}
      <Dialog open={vvfDialog} onClose={() => setVvfDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Nuova Pratica VVF</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo Pratica</InputLabel>
                <Select
                  value={newVVF.tipo_pratica}
                  label="Tipo Pratica"
                  onChange={(e) => setNewVVF({ ...newVVF, tipo_pratica: e.target.value })}
                >
                  <MenuItem value="nuova_installazione">Nuova Installazione</MenuItem>
                  <MenuItem value="rinnovo_cpi">Rinnovo CPI</MenuItem>
                  <MenuItem value="ampliamento">Ampliamento</MenuItem>
                  <MenuItem value="variazione">Variazione</MenuItem>
                  <MenuItem value="voltura">Voltura</MenuItem>
                  <MenuItem value="scia">SCIA</MenuItem>
                  <MenuItem value="altro">Altro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Stato</InputLabel>
                <Select
                  value={newVVF.stato}
                  label="Stato"
                  onChange={(e) => setNewVVF({ ...newVVF, stato: e.target.value })}
                >
                  <MenuItem value="da_presentare">Da Presentare</MenuItem>
                  <MenuItem value="presentata">Presentata</MenuItem>
                  <MenuItem value="in_istruttoria">In Istruttoria</MenuItem>
                  <MenuItem value="approvata">Approvata</MenuItem>
                  <MenuItem value="rilasciato_cpi">CPI Rilasciato</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Numero Pratica"
                value={newVVF.numero_pratica}
                onChange={(e) => setNewVVF({ ...newVVF, numero_pratica: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Data Presentazione"
                type="date"
                value={newVVF.data_presentazione}
                onChange={(e) => setNewVVF({ ...newVVF, data_presentazione: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Data Scadenza"
                type="date"
                value={newVVF.data_scadenza}
                onChange={(e) => setNewVVF({ ...newVVF, data_scadenza: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Comando VVF"
                value={newVVF.comando_vvf}
                onChange={(e) => setNewVVF({ ...newVVF, comando_vvf: e.target.value })}
                placeholder="Es: Comando Provinciale VV.F. di Caserta"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Professionista"
                value={newVVF.professionista}
                onChange={(e) => setNewVVF({ ...newVVF, professionista: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVvfDialog(false)}>Annulla</Button>
          <Button
            variant="contained"
            onClick={() => createVVFMutation.mutate(newVVF)}
            disabled={createVVFMutation.isPending}
          >
            Crea Pratica
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Nuova Rilevazione Tecnico */}
      <Dialog open={readingDialog} onClose={() => setReadingDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Nuova Rilevazione Livello
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            Inserisci la percentuale di livello rilevata dal tecnico durante la visita all'impianto.
            Il sistema confronterà il valore con quello calcolato per rilevare eventuali anomalie.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Data Rilevazione"
                type="date"
                value={newReading.reading_date}
                onChange={(e) => setNewReading({ ...newReading, reading_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Percentuale Rilevata"
                type="number"
                value={newReading.percentage}
                onChange={(e) => setNewReading({ ...newReading, percentage: e.target.value })}
                InputProps={{
                  endAdornment: '%',
                  inputProps: { min: 0, max: 100, step: 1 }
                }}
                required
                helperText={plantData?.capacita_totale ? 
                  `Capacità: ${plantData.capacita_totale} L - ${newReading.percentage ? Math.round(plantData.capacita_totale * newReading.percentage / 100) : 0} L stimati` 
                  : ''
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome Tecnico"
                value={newReading.technician}
                onChange={(e) => setNewReading({ ...newReading, technician: e.target.value })}
                placeholder="Es: Mario Rossi"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Note"
                multiline
                rows={2}
                value={newReading.notes}
                onChange={(e) => setNewReading({ ...newReading, notes: e.target.value })}
                placeholder="Eventuali osservazioni..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReadingDialog(false)}>Annulla</Button>
          <Button
            variant="contained"
            onClick={() => addReadingMutation.mutate(newReading)}
            disabled={addReadingMutation.isPending || !newReading.percentage || !newReading.reading_date}
          >
            Salva Rilevazione
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlantDetail;
